import { displayPrice, chargeableTotal } from '../utils/priceDisplay';
import type { PriceMode } from '../services/api';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CalendarDays, Bell, BarChart3, Zap, PartyPopper, Hand, AlertTriangle, Lock, MessageCircle, Sparkles } from 'lucide-react';
import PaymentStep from './PaymentStep';
import MobileMenu from './MobileMenu';
import BusinessInfo from './BusinessInfo';
import StoryCardGenerator from './StoryCardGenerator';
import HorariosMultiSede from './HorariosMultiSede';
import FichaMovil from './FichaMovil';
import ListaServicios from './ListaServicios';
import TextoExpandible from './TextoExpandible';
import './PublicProfessionalLanding.css';  // ⬅️ AGREGAR ESTA LÍNEA
import './AnimatedCovers.css';

// Agregar DESPUÉS de la línea 302 (después de los imports):
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/**
 * Avisa que alguien entró a esta página, para que el profesional pueda ver si
 * su link circula. No identifica a nadie: el backend guarda solo la fecha y
 * de qué red vino.
 *
 * Una vez por pestaña. Si el visitante recarga o vuelve con el botón atrás no
 * se cuenta de nuevo, así el número significa "personas que entraron" y no
 * "veces que se cargó la página".
 *
 * Nunca se espera ni se muestra su error: si esto falla, el paciente no tiene
 * por qué enterarse.
 */
const registrarVisita = (professionalId: number) => {
  try {
    const marca = `consultar:visita:${professionalId}`;
    if (sessionStorage.getItem(marca)) return;
    sessionStorage.setItem(marca, '1');
    fetch(`${API_URL}/api/professionals/${professionalId}/visit`, {
      method: 'POST',
      keepalive: true,
    }).catch(() => {});
  } catch {
    // sessionStorage puede tirar excepción en modo privado de algunos
    // navegadores. Que no se cuente una visita es irrelevante; que se caiga
    // la página de un médico, no.
  }
};

// Smooth scroll hook
const useSmoothScroll = () => {
  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
    
    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);
};
// Custom Calendar Component
interface CustomCalendarProps {
  selectedDate: string;
  onDateSelect: (date: string) => void;
  professionalSchedule: any[];
  blockedDates?: string[];
  specialDayDates?: string[];
  onlySpecialDays?: boolean;
  minDate?: string;
  maxDate?: string;
  /** Días habilitados para el servicio elegido (ya contempla su horario propio,
   *  que puede abrir días fuera del horario general). null = sin servicio o
   *  todavía cargando → se usa el horario general. */
  serviceAvailableDates?: string[] | null;
}

const CustomCalendar: React.FC<CustomCalendarProps> = ({
  selectedDate,
  onDateSelect,
  professionalSchedule,
  blockedDates = [],
  specialDayDates = [],
  onlySpecialDays = false,
  minDate,
  maxDate,
  serviceAvailableDates = null
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Nombres de días y meses
  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  // Verificar si una fecha está disponible
  const isDateAvailable = (date: Date): boolean => {
    const dateStr = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
    // Bloqueada por excepción
    if (blockedDates.includes(dateStr)) return false;
    // Día especial habilitado
    if (specialDayDates.includes(dateStr)) return true;
    // Si solo días especiales: no mostrar días normales
    if (onlySpecialDays) return false;

    // Con servicio elegido mandan los días que calculó el backend: ahí ya está
    // resuelto el horario propio del servicio (puede habilitar días que no
    // están en el horario general del profesional).
    if (serviceAvailableDates !== null) {
      return serviceAvailableDates.includes(dateStr);
    }

    const dayOfWeek = date.getDay();
    const daySchedule = professionalSchedule.find(day => {
      const dayMapping = {
        'sunday': 0, 'monday': 1, 'tuesday': 2, 'wednesday': 3,
        'thursday': 4, 'friday': 5, 'saturday': 6
      };
      return dayMapping[day.day as keyof typeof dayMapping] === dayOfWeek;
    });

    return daySchedule ? daySchedule.isWorking : false;
  };

  // Verificar si una fecha está bloqueada por excepción
  const isDateBlocked = (date: Date): boolean => {
    const dateStr = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
    return blockedDates.includes(dateStr);
  };

  // Verificar si una fecha está en el pasado
  const isPastDate = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const toLocalDateStr = (d: Date): string => {
    return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
  };

  // Verificar si está dentro del rango permitido
  const isInRange = (date: Date): boolean => {
    const dateStr = toLocalDateStr(date);
    const min = minDate || toLocalDateStr(new Date());
    const maxD = maxDate || (() => { const d = new Date(); d.setDate(d.getDate() + 30); return toLocalDateStr(d); })();

    return dateStr >= min && dateStr <= maxD;
  };

  // Generar días del calendario
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
   
    // Primer día del mes
    const firstDay = new Date(year, month, 1);
    // Último día del mes
    const lastDay = new Date(year, month + 1, 0);
   
    // Días a mostrar (incluyendo días del mes anterior y siguiente para completar la grilla)
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
   
    const days = [];
    const currentDate = new Date(startDate);
   
    // Generar 42 días (6 semanas x 7 días)
    for (let i = 0; i < 42; i++) {
      const date = new Date(currentDate);
      const isCurrentMonth = date.getMonth() === month;
      const isAvailable = isCurrentMonth && isDateAvailable(date) && !isPastDate(date) && isInRange(date);
      const isPast = isPastDate(date);
      const isBlocked = isCurrentMonth && !isPast && isDateBlocked(date);
      // FIX: Comparación de fechas sin timezone
      const dateStr = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
      const isSelected = dateStr === selectedDate;
      const isToday = date.toDateString() === new Date().toDateString();
     
      days.push({
        date: new Date(date),
        day: date.getDate(),
        isCurrentMonth,
        isAvailable,
        isPast,
        isBlocked,
        isSelected,
        isToday
      });
     
      currentDate.setDate(currentDate.getDate() + 1);
    }
   
    return days;
  };

  const days = generateCalendarDays();

  // Navegar mes anterior
  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  // Navegar mes siguiente
  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  // Manejar click en día
  const handleDayClick = (dayInfo: any) => {
    if (dayInfo.isAvailable) {
      const d = dayInfo.date;
      onDateSelect(`${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`);
    }
  };

  return (
    <div className="calendar-premium rounded-2xl p-4">
      {/* Header del calendario */}
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={previousMonth}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
       
        <h3 className="text-lg font-semibold text-gray-900">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
       
        <button
          type="button"
          onClick={nextMonth}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Nombres de días de la semana */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {dayNames.map(day => (
          <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Días del calendario */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((dayInfo, index) => {
          let dayClasses = "w-8 h-8 flex items-center justify-center text-sm rounded-md cursor-pointer transition-all duration-200 ";
         
          // Solo se destacan los días que se pueden elegir (verde) y el elegido
          // (.dia-elegido: en salud, con los colores de la marca). Los demás
          // van en gris, sin fondo: con el rosado de "no disponible" y el rojo
          // de los bloqueados, el calendario se veía alarmante (oct-2026).
          if (!dayInfo.isCurrentMonth) {
            dayClasses += "text-gray-300 cursor-not-allowed ";
          } else if (dayInfo.isPast) {
            dayClasses += "text-gray-300 cursor-not-allowed ";
          } else if (dayInfo.isBlocked) {
            dayClasses += "text-gray-400 cursor-not-allowed line-through ";
          } else if (dayInfo.isAvailable) {
            if (dayInfo.isSelected) {
              dayClasses += "dia-elegido text-white font-semibold shadow-md ";
            } else {
              dayClasses += "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200 hover:bg-emerald-100 font-semibold ";
            }
          } else {
            dayClasses += "text-gray-400 cursor-not-allowed ";
          }
         
          if (dayInfo.isToday && !dayInfo.isSelected) {
            dayClasses += "ring-2 ring-sky-300 ";
          }

          return (
            <button
              key={index}
              type="button"
              onClick={() => handleDayClick(dayInfo)}
              disabled={!dayInfo.isAvailable || !dayInfo.isCurrentMonth}
              className={dayClasses}
            >
              {dayInfo.day}
            </button>
          );
        })}
      </div>

      {/* Leyenda */}
      <div className="mt-4 flex items-center justify-center gap-x-4 gap-y-1 text-xs flex-wrap">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-emerald-50 ring-1 ring-inset ring-emerald-200 rounded mr-1.5"></div>
          <span className="text-gray-600">Disponible</span>
        </div>
        <div className="flex items-center">
          <div className="dia-elegido w-3 h-3 rounded mr-1.5"></div>
          <span className="text-gray-600">Elegido</span>
        </div>
        {blockedDates.length > 0 && (
          <div className="flex items-center">
            <span className="text-gray-400 line-through mr-1.5">15</span>
            <span className="text-gray-600">No atiende</span>
          </div>
        )}
      </div>
    </div>
  );
};

interface Professional {
  id: number;
  nombre: string;
  apellido: string;
  especialidad: string;
  professional_type: 'salud' | 'belleza';
  business_name?: string;
  business_description?: string;
  business_address?: string;
  picture?: string;
  cover_photo?: string;
  telefono?: string;
  subscription_status?: string;
  trial_ends_at?: string;
  is_available_for_booking?: boolean;
  slug?: string;
}

interface Service {
  id: number;
  name: string;
  description: string;
  duration: number;
  price: number;
  category: string;
  // true si el servicio tiene horario propio (ej: EEG) → se agenda solo
  has_custom_schedule?: boolean;
  // Sedes donde tiene horario propio; null = en todas (franjas por sede)
  schedule_location_ids?: number[] | null;
  // 'fijo' (default) | 'obra_social' | 'consultar' — ver utils/priceDisplay
  price_mode?: PriceMode;
  // Propiedades de depósito eliminadas del modelo de servicio para centralizar en PaymentConfig
  // requires_deposit: boolean;
  // deposit_percentage?: number;
}

interface PortfolioItem {
  id: number;
  title: string;
  description: string;
  image_url: string;
  category: string;
}

interface AvailableSlot {
  date: string;
  time: string;
  available: boolean;
}

// Nueva interfaz para la configuración de pago
interface PaymentConfiguration {
  tipo_cobro: 'solo_sena' | 'consulta_completa' | 'efectivo';
  porcentaje_sena: number;
}

// Interfaz para campos dinámicos de booking
interface BookingField {
  key: string;
  label: string;
  type: 'text' | 'date' | 'textarea' | 'select';
  required: boolean;
  predefined: boolean;
  options?: string[];
}

interface BookingExtraFieldsConfig {
  enabled: boolean;
  fields: BookingField[];
}

// Interfaz para ubicación pública
interface PublicLocation {
  id: number;
  name: string;
  address: string;
  city: string;
  phone: string;
  is_active: boolean;
  is_default: boolean;
}

// =====================================================
// MODAL DE SUGERENCIA DE REGISTRO
// =====================================================
interface RegistrationSuggestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRegister: () => void;
  clientName: string;
  appointmentCount: number;
}

const RegistrationSuggestionModal: React.FC<RegistrationSuggestionModalProps> = ({
  isOpen,
  onClose,
  onRegister,
  clientName,
  appointmentCount
}) => {
  if (!isOpen) return null;

  const benefits = [
    {
      icon: <CalendarDays className="w-6 h-6 text-blue-500" />,
      title: 'Gestiona tus turnos',
      description: 'Ve todos tus turnos en un solo lugar'
    },
    {
      icon: <Bell className="w-6 h-6 text-yellow-500" />,
      title: 'Recordatorios automáticos',
      description: 'Nunca más olvides una cita'
    },
    {
      icon: <BarChart3 className="w-6 h-6 text-green-500" />,
      title: 'Historial completo',
      description: 'Accede a tu historial médico/de tratamientos'
    },
    {
      icon: <Zap className="w-6 h-6 text-purple-500" />,
      title: 'Reserva más rápido',
      description: 'Tus datos ya estarán guardados'
    }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-backdrop-in">
      <div className="bg-white rounded-2xl max-w-lg w-full p-8 animate-modal-in">
        <div className="text-center mb-6">
          <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-green-500 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <span className="text-white"><PartyPopper className="w-10 h-10" /></span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center justify-center gap-2">
            ¡Hola {clientName}! <Hand className="w-7 h-7 text-yellow-500" />
          </h2>
          <p className="text-gray-600">
            {appointmentCount === 1 
              ? 'Vemos que es tu segunda cita con nosotros' 
              : `Ya has agendado ${appointmentCount + 1} citas con nosotros`}
          </p>
        </div>

        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            ¿Sabías que puedes crear una cuenta y obtener estos beneficios?
          </h3>
          <div className="grid grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
                <span className="text-2xl">{benefit.icon}</span>
                <div>
                  <h4 className="font-medium text-gray-900 text-sm">{benefit.title}</h4>
                  <p className="text-xs text-gray-600">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <button
            onClick={onRegister}
            className="w-full bg-gradient-to-r from-blue-500 to-green-500 text-white py-4 px-6 rounded-xl font-semibold text-lg hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
          >
            <Sparkles className="w-5 h-5" /> Crear mi cuenta gratis
          </button>
          <button
            onClick={onClose}
            className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-xl font-medium hover:bg-gray-200 transition-colors"
          >
            Tal vez más tarde
          </button>
        </div>

        <p className="text-center text-xs text-gray-500 mt-4">
          Tus datos están seguros. Solo usaremos tu información para mejorar tu experiencia.
        </p>
      </div>
    </div>
  );
};
// ✅ COMPONENTE NUEVO: Banner de profesional no disponible
interface UnavailableBannerProps {
  professionalName: string;
}

const UnavailableBanner: React.FC<UnavailableBannerProps> = ({ professionalName }) => {
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-800">
            No disponible para reservas
          </p>
          <p className="text-xs text-amber-600 mt-0.5">
            <strong>{professionalName}</strong> no está aceptando turnos en este momento. Podés contactarlo directamente.
          </p>
        </div>
      </div>
    </div>
  );
};
const PublicProfessionalLanding: React.FC = () => {
   console.log('🔄 PublicProfessionalLanding RENDERIZADO');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Activar smooth scroll
  useSmoothScroll();
 
  const [professional, setProfessional] = useState<Professional | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [portfolio, setPortfolio] = useState<PortfolioItem[]>([]);
  const [availableSlots, setAvailableSlots] = useState<AvailableSlot[]>([]);
  // Días habilitados para el SERVICIO elegido. null = todavía no se consultó.
  // El backend ya contempla que el horario propio de un servicio puede abrir
  // un día que no está en el horario general del profesional.
  const [serviceAvailableDates, setServiceAvailableDates] = useState<string[] | null>(null);
 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // 🔥 AGREGADO: Configuración de pago
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfiguration | null>(null);
  
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [additionalServices, setAdditionalServices] = useState<Service[]>([]);
  const [showAddServices, setShowAddServices] = useState(false); // lista de "agregar servicio" colapsada por defecto
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showServiceSelector, setShowServiceSelector] = useState(false);

  // ✅ NUEVO: Estados para el lightbox de portfolio
  const [showLightbox, setShowLightbox] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  // Lightbox para avatar
  const [showAvatarLightbox, setShowAvatarLightbox] = useState(false);

  // Estados adicionales
  const [professionalSchedule, setProfessionalSchedule] = useState<any[]>([]);
  const [blockedDates, setBlockedDates] = useState<string[]>([]);
  const [specialDayDates, setSpecialDayDates] = useState<string[]>([]);
  const [onlySpecialDays, setOnlySpecialDays] = useState(false);
  const [maxAdvanceBooking, setMaxAdvanceBooking] = useState<number>(30);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([]);
  const [loadingTimeSlots, setLoadingTimeSlots] = useState(false);

  // Estado para el usuario autenticado
  const [currentUser, setCurrentUser] = useState<any>(null);

  // Story Card Generator
  const [showStoryGenerator, setShowStoryGenerator] = useState(false);

  // Estados para el formulario de agendamiento
  const [bookingForm, setBookingForm] = useState({
    clientName: '',
    clientPhone: '',
    clientEmail: '',
    notes: '',
    appointmentDate: '',
    appointmentTime: '',
    isFirstTime: true,
    urgencyLevel: 'normal'
  });

  const [bookingLoading, setBookingLoading] = useState(false);

  // Precargar datos del paciente logueado al abrir el modal
  useEffect(() => {
    if (showBookingModal && currentUser && !currentUser.es_profesional) {
      setBookingForm(prev => ({
        ...prev,
        clientName: prev.clientName || `${currentUser.nombre || ''} ${currentUser.apellido || ''}`.trim(),
        clientEmail: prev.clientEmail || currentUser.email || '',
        clientPhone: prev.clientPhone || currentUser.telefono || '',
      }));
    }
  }, [showBookingModal, currentUser]);

  // Estados para el modal de éxito
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [bookedAppointment, setBookedAppointment] = useState<any>(null);
  // Estados para modal de registro
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [previousAppointments, setPreviousAppointments] = useState(0);

  // ✅ Multi-sede
  const [publicLocations, setPublicLocations] = useState<PublicLocation[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);

  // ✅ Campos dinámicos de booking
  const [bookingExtraFields, setBookingExtraFields] = useState<BookingExtraFieldsConfig | null>(null);
  const [extraFieldValues, setExtraFieldValues] = useState<Record<string, string>>({});

  // Estados para pago
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null);
  const [paymentProofUrl, setPaymentProofUrl] = useState<string | null>(null);
  const [showPaymentStep, setShowPaymentStep] = useState(false);

  // Estado para guardar el monto final y el tipo de cobro para el modal de éxito
  const [finalPaymentInfo, setFinalPaymentInfo] = useState<{
    amount: number;
    tipoCobro: 'solo_sena' | 'consulta_completa' | 'efectivo';
    porcentajeSena?: number;
  } | null>(null);

// Función para volver atrás del modal de pago (NO reserva)
const handlePaymentBack = () => {
  setShowPaymentStep(false);
  setShowBookingModal(true); // ✅ Volver al modal de agendamiento
};

  // Función para completar el pago y reservar

const handlePaymentComplete = (method: string, proofUrl?: string) => {
  console.log('💳 handlePaymentComplete llamada:', { method, proofUrl });
  
  setPaymentMethod(method);
  setPaymentProofUrl(proofUrl || null);
  setShowPaymentStep(false);
  
  // Reservar con datos de pago
  submitBooking(method, proofUrl);
};

  // Función para omitir pago y reservar de todos modos (pago posterior/efectivo)
  const handlePaymentSkip = () => {
    setShowPaymentStep(false);
    
    // Confirmar con el usuario
    if (window.confirm('¿Deseas reservar sin registrar un pago? Podrás abonar en consultorio (efectivo o transferencia).')) {
      submitBooking(); // Sin argumentos = sin pago registrado
    }
  };

  // Verificar turnos previos del email
  const checkPreviousAppointments = async (email: string) => {
    console.log('🔍 checkPreviousAppointments INICIADA con email:', email);
    
    try {
      const url = `${API_URL}/api/appointments/check-previous?email=${encodeURIComponent(email)}`;
      console.log('📡 URL de la petición:', url);
      
      const response = await fetch(url);
      console.log('📥 Respuesta recibida, status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('📊 Datos recibidos:', data);
        console.log('📊 Turnos previos:', data.appointmentCount);
        console.log('✅ Debe sugerir registro:', data.shouldSuggestRegistration);
        
        setPreviousAppointments(data.appointmentCount);
        
        if (data.shouldSuggestRegistration) {
          console.log('✅ RETORNANDO TRUE - debería mostrar modal');
          return true;
        }
      } else {
        console.error('❌ Response no OK:', response.status);
      }
      
      console.log('❌ RETORNANDO FALSE - no mostrar modal');
      return false;
    } catch (error) {
      console.error('❌ Error verificando turnos previos:', error);
      return false;
    }
  };

  // Función para cargar horarios específicos de una fecha
  const loadTimeSlotsForDate = async (professionalId: number, date: string, locationId?: number | null) => {
    if (!date) {
      setAvailableTimeSlots([]);
      return;
    }
    try {
      setLoadingTimeSlots(true);
      const locParam = locationId ? `&locationId=${locationId}` : '';
      // 🆕 Multi-servicio: si hay servicios adicionales, mandamos todos los IDs
      // para que el backend sume las duraciones y calcule slots correctos.
      const idsParam = additionalServices.length > 0 && selectedService?.id
        ? `&serviceIds=${[selectedService.id, ...additionalServices.map(s => s.id)].join(',')}`
        : `&serviceId=${selectedService?.id || ''}`;
      const response = await fetch(
        `${API_URL}/api/appointments/professional/${professionalId}/available-slots?date=${date}${idsParam}${locParam}&publicBooking=1`
      );
    
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.availableSlots) {
          const times = data.availableSlots
            .filter((slot: any) => slot.available)
            .map((slot: any) => slot.time);
          setAvailableTimeSlots(times);
        } else {
          setAvailableTimeSlots([]);
        }
      } else {
        setAvailableTimeSlots([]);
      }
    } catch (error) {
      console.error('Error cargando horarios para la fecha:', error);
      setAvailableTimeSlots([]);
    } finally {
      setLoadingTimeSlots(false);
    }
  };

  // useEffect para autenticación
  useEffect(() => {
    const checkAuth = () => {
      console.log('🔍 Verificando autenticación...');
     
      const possibleKeys = ['consultAR_user', 'user', 'currentUser', 'authUser'];
      let userData = null;
      let foundKey = null;
     
      for (const key of possibleKeys) {
        const stored = localStorage.getItem(key);
        console.log(`📦 Checking localStorage['${key}']:`, stored);
       
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            if (parsed && (parsed.nombre || parsed.email)) {
              userData = parsed;
              foundKey = key;
              break;
            }
          } catch (error) {
            console.warn(`❌ Error parsing ${key}:`, error);
          }
        }
      }
     
      if (userData && foundKey) {
        console.log('✅ Usuario encontrado en', foundKey, ':', userData);
        setCurrentUser(userData);
      } else {
        console.log('❌ No se encontró usuario autenticado');
        setCurrentUser(null);
      }
    };

    checkAuth();
   
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, [id]); // Agregué [id] como dependencia para evitar advertencias de eslint

  useEffect(() => {
  if (id) {
    loadProfessionalData(id); // Ya no parseamos a número aquí
  }
}, [id]);

  // ✅ Recargar schedule y resetear fecha/hora al cambiar de sede
  // OJO: >= 1 (no > 1). Con un link exclusivo de sede (?sede=15) queda 1 sola
  // sede; si pedíamos > 1, el calendario se quedaba pintado con el horario de la
  // sede DEFAULT (que el backend resuelve cuando no se manda locationId), por eso
  // mostraba los días de FLEBOGEN en vez de los de la sede filtrada.
  useEffect(() => {
    if (professional?.id && selectedLocationId && publicLocations.length >= 1) {
      console.log('🔄 Sede cambiada a:', selectedLocationId, '- recargando horarios...');
      loadProfessionalSchedule(professional.id, selectedLocationId);
      loadAvailableSlots(professional.id, selectedLocationId);
      // Resetear fecha y hora seleccionadas porque los horarios pueden ser distintos
      setBookingForm(prev => ({ ...prev, appointmentDate: '', appointmentTime: '' }));
      setAvailableTimeSlots([]);
    }
  }, [selectedLocationId]);

  // Sedes donde se ofrece el servicio elegido. Con franjas por sede, un
  // servicio puede darse solo en algunas (el EEG de Gallo: Freschi y Flebogen,
  // no el IAP); ofrecer las otras dejaría al paciente frente a un calendario
  // sin un solo día. Si el dato no vino o no coincide con ninguna, todas.
  const sedesDelServicio = React.useMemo(() => {
    const ids = selectedService?.schedule_location_ids;
    if (!selectedService?.has_custom_schedule || !Array.isArray(ids)) return publicLocations;
    const deEsteServicio = publicLocations.filter(l => ids.includes(l.id));
    return deEsteServicio.length > 0 ? deEsteServicio : publicLocations;
  }, [selectedService, publicLocations]);

  // Si la sede marcada no ofrece el servicio, pasar a la primera que sí (el
  // cambio de sede ya recarga horarios con el efecto de arriba)
  useEffect(() => {
    if (!selectedLocationId || sedesDelServicio.length === 0) return;
    if (!sedesDelServicio.some(l => l.id === selectedLocationId)) {
      setSelectedLocationId(sedesDelServicio[0].id);
    }
  }, [sedesDelServicio, selectedLocationId]);

  // Bloquear scroll del body cuando cualquier modal está abierto
  useEffect(() => {
    const anyModalOpen = showBookingModal || showServiceSelector;
    if (anyModalOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${scrollY}px`;
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [showBookingModal, showServiceSelector]);

const loadProfessionalData = async (idOrSlug: string) => {
  setLoading(true);
  setError('');

  try {
    // 🆕 Usar el parámetro directamente (puede ser ID o slug)
    console.log('🔍 Cargando profesional con:', idOrSlug);
    const profResponse = await fetch(`${API_URL}/api/professionals/${idOrSlug}/public`);
    
    if (!profResponse.ok) {
      throw new Error('Profesional no encontrado');
    }
    
    const profData = await profResponse.json();
    console.log('✅ Datos del profesional:', profData);
    setProfessional(profData.professional);

    // 🆕 Usar el ID numérico del profesional para los otros endpoints
    const professionalId = profData.professional.id;
    console.log('📋 ID del profesional:', professionalId);

    registrarVisita(professionalId);

    // Cargar servicios (sin romper si falla)
    try {
      const servicesResponse = await fetch(`${API_URL}/api/professionals/${professionalId}/services/public`);
      if (servicesResponse.ok) {
        const servicesData = await servicesResponse.json();
        setServices(servicesData.services || []);
        console.log('✅ Servicios cargados:', servicesData.services?.length);
      }
    } catch (err) {
      console.warn('⚠️ Error cargando servicios:', err);
    }

    // Cargar portfolio (sin romper si falla)
    try {
      const portfolioResponse = await fetch(`${API_URL}/api/professionals/${professionalId}/portfolio/public`);
      if (portfolioResponse.ok) {
        const portfolioData = await portfolioResponse.json();
        setPortfolio(portfolioData.portfolio || []);
        console.log('✅ Portfolio cargado:', portfolioData.portfolio?.length);
      }
    } catch (err) {
      console.warn('⚠️ Error cargando portfolio:', err);
    }
    
    // ✅ Cargar sedes públicas (sin romper si falla)
    // resolvedLocationId: la sede que termina seleccionada en la carga inicial.
    // La usamos abajo para cargar schedule+slots YA con la sede correcta y evitar
    // la carrera contra la carga sin sede (que traía la sede default = otro día).
    let resolvedLocationId: number | null = null;
    try {
      const locationsResponse = await fetch(`${API_URL}/api/locations/professional/${professionalId}/public`);
      if (locationsResponse.ok) {
        const locationsData = await locationsResponse.json();
        let locs: PublicLocation[] = (locationsData.locations || []).filter((l: PublicLocation) => l.is_active);

        // 🆕 Link exclusivo de una sede: ?sede=<id> o ?sede=<slug-del-nombre>
        // Si el parámetro coincide con una sede, dejamos SOLO esa: así el selector
        // se oculta (porque queda 1 sola) y el paciente ve únicamente esa agenda.
        const sedeParam = new URLSearchParams(window.location.search).get('sede');
        if (sedeParam) {
          const slugify = (s: string) => {
            const combiningMarks = /[̀-ͯ]/g; // acentos separados por NFD
            return s.toLowerCase().normalize('NFD').replace(combiningMarks, '')
              .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
          };
          const target = sedeParam.toLowerCase().trim();
          const matched = locs.filter(
            (l) => String(l.id) === target || slugify(l.name) === slugify(target)
          );
          if (matched.length > 0) {
            locs = matched;
            console.log('🎯 Link de sede específica:', matched[0].name);
          } else {
            console.warn('⚠️ ?sede no coincide con ninguna sede activa:', sedeParam);
          }
        }

        setPublicLocations(locs);
        // Auto-seleccionar sede default o la única activa
        if (locs.length === 1) {
          resolvedLocationId = locs[0].id;
        } else if (locs.length > 1) {
          const defaultLoc = locs.find((l: PublicLocation) => l.is_default);
          if (defaultLoc) resolvedLocationId = defaultLoc.id;
        }
        if (resolvedLocationId) setSelectedLocationId(resolvedLocationId);
        console.log('✅ Sedes cargadas:', locs.length);
      }
    } catch (err) {
      console.warn('⚠️ Error cargando sedes:', err);
    }

    // ✅ Cargar campos extra de booking (sin romper si falla)
    try {
      const extraFieldsResponse = await fetch(`${API_URL}/api/professionals/${professionalId}/booking-extra-fields/public`);
      if (extraFieldsResponse.ok) {
        const extraData = await extraFieldsResponse.json();
        if (extraData.success && extraData.booking_extra_fields?.enabled) {
          setBookingExtraFields(extraData.booking_extra_fields);
          console.log('✅ Campos extra cargados:', extraData.booking_extra_fields.fields?.length);
        } else {
          setBookingExtraFields(null);
        }
      }
    } catch (err) {
      console.warn('⚠️ Error cargando campos extra:', err);
    }

    // Cargar configuración de pago (sin romper si falla)
    try {
      const paymentConfigResponse = await fetch(`${API_URL}/api/payments/config/${professionalId}`);
      if (paymentConfigResponse.ok) {
        const configData = await paymentConfigResponse.json();
        setPaymentConfig(configData.data);
        console.log('✅ Config de pago cargada');
      } else {
        console.warn('⚠️ No se pudo cargar la configuración de pagos. Usando por defecto "efectivo".');
        setPaymentConfig({
          tipo_cobro: 'efectivo',
          porcentaje_sena: 0
        });
      }
    } catch (err) {
      console.warn('⚠️ Error cargando config de pago:', err);
      setPaymentConfig({
        tipo_cobro: 'efectivo',
        porcentaje_sena: 0
      });
    }

    // Cargar slots y schedule (sin romper si fallan)
    // Pasamos resolvedLocationId para cargar YA con la sede correcta. Si no se
    // pasa, el backend usa la sede default (otro día/horario) y el calendario
    // queda pintado mal (bug del link exclusivo por sede).
    try {
      await loadAvailableSlots(professionalId, resolvedLocationId);
    } catch (err) {
      console.warn('⚠️ Error cargando slots:', err);
    }

    try {
      await loadProfessionalSchedule(professionalId, resolvedLocationId);
    } catch (err) {
      console.warn('⚠️ Error cargando schedule:', err);
    }

  } catch (err: any) {
    console.error('❌ Error cargando profesional:', err);
    setError(err.message || 'Error al cargar la información');
  } finally {
    setLoading(false);
  }
};


  const loadProfessionalSchedule = async (professionalId: number, locationId?: number | null) => {
    try {
      setLoadingSchedule(true);
      const locParam = locationId ? `?locationId=${locationId}` : '';
      const response = await fetch(`${API_URL}/api/schedule/professional/${professionalId}/config/public${locParam}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.scheduleConfig && data.scheduleConfig.workingDays) {
          setProfessionalSchedule(data.scheduleConfig.workingDays);
          if (data.scheduleConfig.maxAdvanceBooking) {
            setMaxAdvanceBooking(data.scheduleConfig.maxAdvanceBooking);
          }
          setOnlySpecialDays(data.scheduleConfig.onlySpecialDays || false);
          console.log('Horarios del profesional cargados:', data.scheduleConfig.workingDays);
        }
      } else {
        console.log('No se pudieron cargar horarios, usando configuración por defecto');
        setProfessionalSchedule([
          { day: 'sunday', dayName: 'Domingo', isWorking: false },
          { day: 'monday', dayName: 'Lunes', isWorking: false },
          { day: 'tuesday', dayName: 'Martes', isWorking: false },
          { day: 'wednesday', dayName: 'Miércoles', isWorking: false },
          { day: 'thursday', dayName: 'Jueves', isWorking: false },
          { day: 'friday', dayName: 'Viernes', isWorking: true },
          { day: 'saturday', dayName: 'Sábado', isWorking: false }
        ]);
      }

      // Cargar días bloqueados (excepciones)
      try {
        const excResponse = await fetch(`${API_URL}/api/schedule/professional/${professionalId}/exceptions/public${locParam}`);
        if (excResponse.ok) {
          const excData = await excResponse.json();
          if (excData.success && excData.exceptions) {
            const blocked = excData.exceptions
              .filter((e: any) => !e.is_working)
              .map((e: any) => {
                const d = e.exception_date;
                return typeof d === 'string' ? d.split('T')[0] : new Date(d).toISOString().split('T')[0];
              });
            setBlockedDates(blocked);
            // Días especiales: excepciones con is_working=true y custom_hours
            const special = excData.exceptions
              .filter((e: any) => e.is_working && e.exception_type === 'custom_hours')
              .map((e: any) => {
                const d = e.exception_date;
                return typeof d === 'string' ? d.split('T')[0] : new Date(d).toISOString().split('T')[0];
              });
            setSpecialDayDates(special);
            console.log('Días bloqueados cargados:', blocked);
            console.log('Días especiales cargados:', special);
          }
        }
      } catch (excErr) {
        console.error('Error cargando excepciones:', excErr);
      }
    } catch (error) {
      console.error('Error cargando horarios:', error);
      setProfessionalSchedule([
        { day: 'sunday', dayName: 'Domingo', isWorking: false },
        { day: 'monday', dayName: 'Lunes', isWorking: false },
        { day: 'tuesday', dayName: 'Martes', isWorking: false },
        { day: 'wednesday', dayName: 'Miércoles', isWorking: false },
        { day: 'thursday', dayName: 'Jueves', isWorking: false },
        { day: 'friday', dayName: 'Viernes', isWorking: true },
        { day: 'saturday', dayName: 'Sábado', isWorking: false }
      ]);
    } finally {
      setLoadingSchedule(false);
    }
  };

  // Días habilitados para el servicio elegido (respeta su horario propio).
  // Se recarga al cambiar de servicio o de sede.
  useEffect(() => {
    if (!professional?.id || !selectedService?.id) {
      setServiceAvailableDates(null);
      return;
    }
    let cancelled = false;
    setServiceAvailableDates(null); // mientras carga, cae al horario general
    const today = new Date();
    const endDate = new Date();
    // Hasta donde se puede reservar. Eran 60 días fijos: con 90 o 180, los
    // días que pasaban de 60 salían "No disponible" (Vero, oct-2026).
    endDate.setDate(today.getDate() + maxAdvanceBooking);
    const locParam = selectedLocationId ? `&locationId=${selectedLocationId}` : '';
    fetch(
      `${API_URL}/api/schedule/professional/${professional.id}/available-slots/public` +
      `?start=${today.toISOString().split('T')[0]}&end=${endDate.toISOString().split('T')[0]}` +
      `&serviceId=${selectedService.id}${locParam}&publicBooking=1`
    )
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (cancelled || !data?.success) return;
        const dates = Array.from(new Set(
          (data.slots || [])
            .filter((sl: any) => sl.available)
            .map((sl: any) => sl.date)
        )) as string[];
        setServiceAvailableDates(dates);
      })
      .catch(() => { /* si falla, queda null y usa el horario general */ });
    return () => { cancelled = true; };
  }, [professional?.id, selectedService?.id, selectedLocationId, maxAdvanceBooking]);

  const loadAvailableSlots = async (professionalId: number, locationId?: number | null) => {
    try {
      const today = new Date();
      const endDate = new Date();
      endDate.setDate(today.getDate() + 30);
      const locParam = locationId ? `&locationId=${locationId}` : '';

      const response = await fetch(
  `${API_URL}/api/schedule/professional/${professionalId}/available-slots/public?start=${today.toISOString().split('T')[0]}&end=${endDate.toISOString().split('T')[0]}${locParam}&publicBooking=1`
);
     
      if (response.ok) {
        const data = await response.json();
        setAvailableSlots(data.slots || []);
      }
    } catch (error) {
      console.error('Error cargando slots:', error);
    }
  };

  const isDateAvailable = (date: string): boolean => {
    // Día especial habilitado
    if (specialDayDates.includes(date)) return true;
    // Si solo días especiales: no mostrar días normales
    if (onlySpecialDays) return false;

    // Con un servicio elegido, mandan los días que devolvió el backend: ahí ya
    // está resuelto si el servicio tiene horario propio (que puede habilitar
    // días fuera del horario general) o si sigue el horario del profesional.
    if (selectedService && serviceAvailableDates !== null) {
      return serviceAvailableDates.includes(date);
    }

    const selectedDate = new Date(date);
    const dayOfWeek = selectedDate.getDay();

    const daySchedule = professionalSchedule.find(day => {
      const dayMapping = {
        'sunday': 0, 'monday': 1, 'tuesday': 2, 'wednesday': 3,
        'thursday': 4, 'friday': 5, 'saturday': 6
      };
      return dayMapping[day.day as keyof typeof dayMapping] === dayOfWeek;
    });

    return daySchedule ? daySchedule.isWorking : false;
  };

  const getAvailableDaysText = (): string => {
    const availableDays = professionalSchedule
      .filter(day => day.isWorking)
      .map(day => day.dayName);
   
    if (availableDays.length === 0) return 'Sin horarios configurados';
    if (availableDays.length === 1) return availableDays[0];
    if (availableDays.length === 2) return availableDays.join(' y ');
   
    return availableDays.slice(0, -1).join(', ') + ' y ' + availableDays[availableDays.length - 1];
  };

  const handleBookAppointment = (service?: Service) => {
    // Verificar disponibilidad
    if (professional?.is_available_for_booking === false) {
      alert('Este profesional no está disponible para agendar citas en este momento.');
      return;
    }
    if (service) {
      // Servicio específico → abrir modal directo
      setSelectedService(service);
      setAdditionalServices([]);
      setShowBookingModal(true);
      setBookingForm(prev => ({
        ...prev,
        appointmentDate: '',
        appointmentTime: ''
      }));
    } else if (services.length === 1) {
      // Un solo servicio → abrir modal directo con ese
      setSelectedService(services[0]);
      setAdditionalServices([]);
      setShowBookingModal(true);
      setBookingForm(prev => ({
        ...prev,
        appointmentDate: '',
        appointmentTime: ''
      }));
    } else {
      // Múltiples servicios sin selección → mostrar selector
      setShowServiceSelector(true);
    }
  };

  const handleServiceSelection = (service: Service) => {
    setSelectedService(service);
    setAdditionalServices([]);
    setShowServiceSelector(false);
    setShowBookingModal(true);
    setBookingForm(prev => ({
      ...prev,
      appointmentDate: '',
      appointmentTime: ''
    }));
  };
  
  const formatPrice = (price: number | string) => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return numPrice.toLocaleString('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    });
  };

  // Multi-servicio: totales (parseFloat para evitar concatenación de strings del backend)
  const allSelectedServices = selectedService ? [selectedService, ...additionalServices] : [];
  // Los servicios en modo obra social / a consultar no suman al total a cobrar.
  const totalPrice = chargeableTotal(allSelectedServices as any);
  const totalDuration = allSelectedServices.reduce((sum, s) => sum + (typeof s.duration === 'string' ? parseInt(s.duration) : s.duration), 0);

  const toggleAdditionalService = (service: Service) => {
    setAdditionalServices(prev => {
      const exists = prev.find(s => s.id === service.id);
      if (exists) return prev.filter(s => s.id !== service.id);
      return [...prev, service];
    });
  };


const handleBookingSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  
  console.log('🚀 ===== INICIO handleBookingSubmit =====');
  console.log('📋 Datos del formulario:', bookingForm);
  console.log('🎯 Servicio seleccionado:', selectedService);
  
  if (!bookingForm.clientName || !bookingForm.clientPhone || !bookingForm.appointmentDate || !bookingForm.appointmentTime) {
    alert('Por favor completa todos los campos requeridos');
    return;
  }

  if (!selectedService) {
    alert('Por favor selecciona un servicio');
    return;
  }

  // ✅ Validar sede si hay múltiples
  if (publicLocations.length > 1 && !selectedLocationId) {
    alert('Por favor selecciona una sede');
    return;
  }

  // ✅ Validar campos dinámicos requeridos
  if (bookingExtraFields?.enabled && bookingExtraFields.fields?.length > 0) {
    const missingRequired = bookingExtraFields.fields.filter(
      f => f.required && (!extraFieldValues[f.key] || extraFieldValues[f.key].trim() === '')
    );
    if (missingRequired.length > 0) {
      alert(`Por favor completa los campos requeridos: ${missingRequired.map(f => f.label).join(', ')}`);
      return;
    }
  }

  if (!professional) return; // ✅ CHECK DE SEGURIDAD

  // ✅ Revalidar disponibilidad (con multi-servicio)
  try {
    const idsParamRevalidate = additionalServices.length > 0
      ? `&serviceIds=${[selectedService.id, ...additionalServices.map(s => s.id)].join(',')}`
      : `&serviceId=${selectedService.id}`;
    // ✅ FIX: incluir locationId. Sin esto, la revalidación pedía los slots de la
    // sede DEFAULT (otros días/horarios) y el horario elegido no aparecía →
    // falso "este horario acaba de ser reservado" que bloqueaba reservas reales.
    const locParamRevalidate = selectedLocationId ? `&locationId=${selectedLocationId}` : '';
    const revalidateResponse = await fetch(
      // ✅ FIX: Usar professional.id en vez de id
      `${API_URL}/api/appointments/professional/${professional.id}/available-slots?date=${bookingForm.appointmentDate}${idsParamRevalidate}${locParamRevalidate}&publicBooking=1`
    );
    
    if (revalidateResponse.ok) {
      const revalidateData = await revalidateResponse.json();
      const isStillAvailable = revalidateData.availableSlots?.some(
        (slot: any) => slot.time === bookingForm.appointmentTime && slot.available
      );
      
      if (!isStillAvailable) {
        alert('Lo sentimos, este horario acaba de ser reservado. Por favor selecciona otro.');
        loadTimeSlotsForDate(professional.id, bookingForm.appointmentDate, selectedLocationId); // ✅ FIX
        setBookingForm(prev => ({ ...prev, appointmentTime: '' }));
        return;
      }
    }
  } catch (error) {
    console.error('Error revalidando:', error);
  }

  setBookingLoading(true);

  try {
    console.log('🔍 Step 1: Obteniendo configuración de pago del profesional...');
    console.log('🔗 URL:', `http://localhost:5000/api/payments/config/${professional.id}`);
    
    // ✅ Obtener configuración de pago del profesional usando professional.id
    const paymentConfigResponse = await fetch(`${API_URL}/api/payments/config/${professional.id}`);
    
    console.log('📥 Response status:', paymentConfigResponse.status);
    console.log('📥 Response ok:', paymentConfigResponse.ok);
    
    if (!paymentConfigResponse.ok) {
      throw new Error('Error al obtener configuración de pago');
    }

    const paymentConfigData = await paymentConfigResponse.json();
    
    console.log('📦 CONFIGURACIÓN COMPLETA recibida:', JSON.stringify(paymentConfigData, null, 2));
    
    if (!paymentConfigData.success) {
      throw new Error('Configuración de pago no disponible');
    }

    const config = paymentConfigData.data;
    
    console.log('⚙️ Config extraída:', config);
    
    // ✅ DECISIÓN: ¿Mostrar modal de pago?
    // Si ningun servicio elegido es cobrable (obra social / a consultar), no se
    // pasa por MercadoPago: si no, se mandaria a cobrar $0. Pedido de Adolfo Gallo.
    const hayAlgoQueCobrar = totalPrice > 0;
    const requiresPayment = hayAlgoQueCobrar &&
      (config.tipo_cobro === 'solo_sena' || config.tipo_cobro === 'consulta_completa');
    
    if (requiresPayment) {
      console.log('✅ SÍ REQUIERE PAGO - Mostrando modal...');
      
      // ✅ CALCULAR MONTO A COBRAR (multi-servicio: sumar todos)
      let amountToPay = totalPrice;

      if (config.tipo_cobro === 'solo_sena' && config.porcentaje_sena) {
        amountToPay = Math.round(totalPrice * (config.porcentaje_sena / 100));
        console.log(`💰 Seña calculada: ${config.porcentaje_sena}% de $${totalPrice} = $${amountToPay}`);
      } else {
        console.log(`💰 Consulta completa: $${amountToPay}`);
      }
      
      // Guardar info de pago para el modal de éxito si el pago es exitoso
      setFinalPaymentInfo({
        amount: amountToPay,
        tipoCobro: config.tipo_cobro,
        porcentajeSena: config.porcentaje_sena
      });

      // ✅ Guardar datos en sessionStorage
      const allServiceNames = allSelectedServices.map(s => s.name).join(' + ');
      const bookingData = {
        serviceId: selectedService.id,
        serviceIds: allSelectedServices.map(s => s.id),
        appointmentDate: bookingForm.appointmentDate,
        appointmentTime: bookingForm.appointmentTime,
        clientName: bookingForm.clientName.trim(),
        clientEmail: bookingForm.clientEmail.trim() || null,
        clientPhone: bookingForm.clientPhone.trim(),
        notes: bookingForm.notes.trim() || null,
        isFirstTime: bookingForm.isFirstTime,
        urgencyLevel: bookingForm.urgencyLevel,
        price: amountToPay,
        fullPrice: totalPrice,
        duration: totalDuration,
        depositRequired: config.tipo_cobro === 'solo_sena',
        depositAmount: config.tipo_cobro === 'solo_sena' ? amountToPay : 0,
        serviceName: allServiceNames,
        professionalName: professional.business_name || `${professional.nombre} ${professional.apellido}`,
        professionalId: professional.id,
        tipoCobro: config.tipo_cobro,
        porcentajeSena: config.porcentaje_sena,
        locationId: selectedLocationId || null,
        patient_extra_data: (bookingExtraFields?.enabled && Object.keys(extraFieldValues).length > 0)
          ? extraFieldValues
          : null
      };

      console.log('💾 Guardando en sessionStorage:', bookingData);
      sessionStorage.setItem('pendingBooking', JSON.stringify(bookingData));
      
      // ✅ Mostrar modal de pago (NO crear turno)
      setShowPaymentStep(true);
      setShowBookingModal(false);
      setBookingLoading(false);
      
      return;
    }

    // ✅ NO requiere pago - Crear turno directamente
    console.log('⏭️ NO requiere pago anticipado, creando turno directamente...');
    // Guardar info de pago (Efectivo) para el modal de éxito
    setFinalPaymentInfo({
      amount: totalPrice,
      tipoCobro: config.tipo_cobro,
    });
    await submitBooking();
    
  } catch (error) {
    console.error('❌ ERROR COMPLETO:', error);
    console.error('❌ Stack:', error instanceof Error ? error.stack : 'N/A');
    alert('Error al procesar la solicitud: ' + (error instanceof Error ? error.message : 'Error desconocido'));
    setBookingLoading(false);
  }
  
  console.log('🏁 ===== FIN handleBookingSubmit =====');
};

const submitBooking = async (method?: string, proofUrl?: string) => {
  if (!selectedService || !professional) return; // ✅ Check profesional

  setBookingLoading(true);

  // Intentar cargar la data de booking del sessionStorage (si viene del flujo de pago)
  let bookingDataFromSession = null;
  let finalDepositAmount = 0;
  let finalDepositRequired = false;

  try {
    const pendingBooking = sessionStorage.getItem('pendingBooking');
    if (pendingBooking) {
      bookingDataFromSession = JSON.parse(pendingBooking);
      finalDepositAmount = bookingDataFromSession.depositAmount || bookingDataFromSession.price;
      finalDepositRequired = bookingDataFromSession.depositRequired;
    }
  } catch(e) {
    console.error('Error al parsear sessionStorage:', e);
  }

  try {
    const priceForAppointment = bookingDataFromSession?.fullPrice || selectedService.price;
    const depositAmountForAppointment = finalDepositAmount;
    const depositRequiredForAppointment = finalDepositRequired;

    const serviceIds = bookingDataFromSession?.serviceIds || allSelectedServices.map(s => s.id);
    const appointmentData = {
      serviceId: selectedService.id,
      serviceIds: serviceIds.length > 1 ? serviceIds : undefined,
      appointmentDate: bookingForm.appointmentDate,
      appointmentTime: bookingForm.appointmentTime,
      clientName: bookingForm.clientName.trim(),
      clientEmail: bookingForm.clientEmail.trim() || null,
      clientPhone: bookingForm.clientPhone.trim(),
      notes: bookingForm.notes.trim() || null,
      isFirstTime: bookingForm.isFirstTime,
      urgencyLevel: bookingForm.urgencyLevel,
      price: priceForAppointment,
      duration: bookingDataFromSession?.duration || totalDuration,

      depositRequired: depositRequiredForAppointment,
      depositAmount: depositAmountForAppointment,

      metodo_pago_elegido: method || null,
      comprobante_pago_url: proofUrl || null,

      pago_verificado: method === 'mercadopago',

      // ✅ Multi-sede
      locationId: selectedLocationId || null,

      // ✅ Campos dinámicos
      patient_extra_data: (bookingExtraFields?.enabled && Object.keys(extraFieldValues).length > 0)
        ? extraFieldValues
        : null
    };

    console.log('📤 Enviando datos del turno:', appointmentData);

    // ✅ FIX: Usar professional.id
    const response = await fetch(`${API_URL}/api/appointments/professional/${professional.id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(appointmentData)
    });

    const result = await response.json();

    if (response.ok && result.success) {
      console.log('✅ Turno creado exitosamente:', result.appointment);
      
      sessionStorage.removeItem('pendingBooking');
      
      const allNames = bookingDataFromSession?.serviceName || allSelectedServices.map(s => s.name).join(' + ') || selectedService.name;
      const appointmentInfo = {
        ...result.appointment,
        serviceName: allNames,
        professionalName: professional.business_name || `${professional.nombre} ${professional.apellido}`,
        formattedDate: (() => {
          const [year, month, day] = bookingForm.appointmentDate.split('-').map(Number);
          const date = new Date(year, month - 1, day);
          return date.toLocaleDateString('es-ES', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          });
        })(),
        formattedPrice: formatPrice(bookingDataFromSession?.fullPrice || totalPrice),
        locationName: selectedLocationId
          ? publicLocations.find(l => l.id === selectedLocationId)?.name || null
          : null,
        paymentInfo: {
          ...(finalPaymentInfo || (bookingDataFromSession ? {
            amount: bookingDataFromSession.price,
            tipoCobro: bookingDataFromSession.tipoCobro,
            porcentajeSena: bookingDataFromSession.porcentajeSena
          } : { amount: selectedService.price, tipoCobro: 'efectivo' })),
          method: method || 'efectivo'
        }
      };
      
      setBookedAppointment(appointmentInfo);
      setShowSuccessModal(true);
      setShowBookingModal(false);
      setShowPaymentStep(false); 
      
      setBookingForm({
        clientName: '',
        clientPhone: '',
        clientEmail: '',
        notes: '',
        appointmentDate: '',
        appointmentTime: '',
        isFirstTime: true,
        urgencyLevel: 'normal'
      });
      setSelectedService(null);
      setAdditionalServices([]);
      setFinalPaymentInfo(null);
      setExtraFieldValues({}); 

      // ✅ FIX: Usar professional.id
      loadAvailableSlots(professional.id);
      
    } else {
      alert(result.message || 'Error al agendar');
    }
  } catch (error) {
    console.error('Error:', error);
    alert('Error de conexión');
  } finally {
    setBookingLoading(false);
  }
};

  // Manejar registro desde el modal
  const handleRegisterFromModal = () => {
    setShowRegistrationModal(false);
    navigate('/register', {
      state: {
        prefilledData: {
          nombre: bookingForm.clientName?.split(' ')[0] || '',
          apellido: bookingForm.clientName?.split(' ').slice(1).join(' ') || '',
          email: bookingForm.clientEmail,
          telefono: bookingForm.clientPhone
        }
      }
    });
  };

  const handleCloseRegistrationModal = () => {
    setShowRegistrationModal(false);
    setShowSuccessModal(true);
  };

  const handleWhatsApp = () => {
    if (!professional?.telefono) return;
   
    const message = encodeURIComponent(
      `Hola ${professional.nombre}, vi tu perfil en ConsultAR y me interesa agendar una cita. ¿Podrías darme más información?`
    );
    const phone = professional.telefono.replace(/\D/g, '');
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  };

  // ✅ NUEVO: Funciones para el lightbox
  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setShowLightbox(true);
    document.body.style.overflow = 'hidden'; // Prevenir scroll
  };

  const closeLightbox = () => {
    setShowLightbox(false);
    document.body.style.overflow = 'unset'; // Restaurar scroll
  };

  const goToNextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % portfolio.length);
  };

  const goToPrevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + portfolio.length) % portfolio.length);
  };

  // Cerrar con tecla ESC
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!showLightbox) return;
      
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') goToNextImage();
      if (e.key === 'ArrowLeft') goToPrevImage();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showLightbox, portfolio.length]);

  // Barra fija de "Agendar Cita" en el celular (el JSX está al final).
  // Se esconde con cualquier ventana abierta encima: ahí sobra.
  const reservable = !!professional && professional.is_available_for_booking !== false;
  // SOLO AL PRINCIPIO DE LA PÁGINA. Cuando aparece la sección Servicios, cada
  // tarjeta ya tiene su "Agendar" a la vista y la barra sobra (pedido de
  // Esteban). Se va al llegar a Servicios y sigue oculta más abajo; vuelve si
  // se sube de nuevo. El margen de -30% abajo hace que se vaya cuando la
  // sección ya entró bien en pantalla, no apenas asoma su título.
  //
  // Ref de función y no useRef + useEffect: la sección se monta recién cuando
  // termina la carga, y un efecto ya habría corrido antes sin encontrarla. Así
  // fue la primera versión: la barra no se iba nunca.
  const [enServiciosOMasAbajo, setEnServiciosOMasAbajo] = useState(false);
  const observadorServicios = React.useRef<IntersectionObserver | null>(null);
  const refSeccionServicios = React.useCallback((seccion: HTMLElement | null) => {
    observadorServicios.current?.disconnect();
    observadorServicios.current = null;
    if (!seccion) {
      setEnServiciosOMasAbajo(false);
      return;
    }
    if (!('IntersectionObserver' in window)) return;
    const observador = new IntersectionObserver(
      ([entrada]) => {
        // A la vista, o ya pasó por arriba de la pantalla.
        setEnServiciosOMasAbajo(entrada.isIntersecting || entrada.boundingClientRect.top < 0);
      },
      { rootMargin: '0px 0px -30% 0px' }
    );
    observador.observe(seccion);
    observadorServicios.current = observador;
  }, []);

  const mostrarBarraReserva =
    reservable && !enServiciosOMasAbajo &&
    !showBookingModal && !showServiceSelector && !showPaymentStep && !showSuccessModal &&
    !showRegistrationModal && !showLightbox && !showAvatarLightbox && !showStoryGenerator;

  // La burbuja de Soporte se monta en App para todas las pantallas, así que
  // se la corre con una clase en el body mientras la barra está. Si no, le
  // tapa la punta derecha al botón: ya pasaba con el botón de siempre en la
  // página de Antonella Bissaro.
  useEffect(() => {
    if (!mostrarBarraReserva) return;
    document.body.classList.add('barra-reserva-visible');
    return () => document.body.classList.remove('barra-reserva-visible');
  }, [mostrarBarraReserva]);

  const getInitials = (nombre: string, apellido: string) => {
    return `${nombre.charAt(0)}${apellido.charAt(0)}`.toUpperCase();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  const getAvailableSlotsForDate = (date: string) => {
    return availableSlots
      .filter(slot => slot.date === date && slot.available)
      .map(slot => slot.time)
      .sort();
  };

  if (loading) {
    // Skeleton que replica el layout real (portada → tarjeta → secciones):
    // la página "aparece" en su forma final en vez de mostrar un spinner.
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/20">
        {/* Portada */}
        <div className="landing-skeleton h-56 md:h-80" />

        <div className="relative -mt-20 pb-12">
          <div className="max-w-6xl mx-auto px-4">
            {/* Tarjeta principal */}
            <div className="bg-white/90 rounded-3xl shadow-2xl p-6 md:p-8 mb-8">
              <div className="flex flex-col md:flex-row md:items-start md:space-x-8">
                <div className="landing-skeleton w-32 h-32 md:w-36 md:h-36 rounded-3xl mx-auto md:mx-0 mb-4 md:mb-0 flex-shrink-0" />
                <div className="flex-1 space-y-4 w-full">
                  <div className="landing-skeleton h-8 rounded-lg w-3/4 mx-auto md:mx-0" />
                  <div className="landing-skeleton h-5 rounded-lg w-1/2 mx-auto md:mx-0" />
                  <div className="landing-skeleton h-4 rounded-lg w-full" />
                  <div className="landing-skeleton h-4 rounded-lg w-5/6" />
                </div>
              </div>
            </div>

            {/* Secciones */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-8">
                <div className="bg-white/90 rounded-3xl p-6 md:p-8">
                  <div className="landing-skeleton h-6 rounded-lg w-40 mb-6" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[0, 1, 2, 3].map((i) => (
                      <div key={i} className="border border-gray-100 rounded-2xl p-5 space-y-3">
                        <div className="landing-skeleton h-5 rounded w-2/3" />
                        <div className="landing-skeleton h-4 rounded w-full" />
                        <div className="landing-skeleton h-9 rounded-xl w-28 ml-auto" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="space-y-6">
                <div className="bg-white/90 rounded-3xl p-6 space-y-3">
                  <div className="landing-skeleton h-5 rounded w-28 mb-4" />
                  <div className="landing-skeleton h-4 rounded w-full" />
                  <div className="landing-skeleton h-4 rounded w-4/5" />
                </div>
                <div className="bg-white/90 rounded-3xl p-6 space-y-3">
                  <div className="landing-skeleton h-5 rounded w-28 mb-4" />
                  <div className="landing-skeleton h-4 rounded w-full" />
                  <div className="landing-skeleton h-4 rounded w-full" />
                  <div className="landing-skeleton h-4 rounded w-3/5" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !professional) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-.833-1.964-.833-2.732 0L5.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Profesional no encontrado</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/search')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Buscar otros profesionales
          </button>
        </div>
      </div>
    );
  }

   return (
    // landing-marca: en salud, los botones de reservar van con los colores
    // del logo, como la portada (.btn-premium en PublicProfessionalLanding.css)
    <div className={`min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/20${professional.professional_type === 'salud' ? ' landing-marca' : ''}`}>
      {/* Mobile Menu - FUERA del header parallax para evitar problemas de z-index */}
      {/* Con un modal abierto no se muestra: iba por encima y en el de reserva
          tapaba la X de cerrar */}
      {!(showBookingModal || showServiceSelector || showPaymentStep || showSuccessModal) && (
      <div className="md:hidden fixed top-4 right-4 z-[999999]">
        <MobileMenu
          currentUser={currentUser}
          onLogout={() => {
            localStorage.clear();
            setCurrentUser(null);
            window.location.reload();
          }}
        />
      </div>
      )}

      {/* Header con foto de portada */}
      {/* Header con foto de portada - Parallax */}
      <div
        id="cover-header"
        className={`landing-fade relative h-56 md:h-80 overflow-hidden transition-transform portada-curva ${
          professional.cover_photo
            ? 'bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700'
            : `animated-cover animated-cover--${professional.professional_type === 'salud' ? 'marca' : 'belleza'} animated-cover--variant-${(professional.id || 0) % 4}`
        }`}
      >
        {professional.cover_photo ? (
          <img
            src={professional.cover_photo}
            alt="Portada"
            className="w-full h-full object-cover"
          />
        ) : professional.professional_type === 'salud' ? (
          // Círculos suaves de la portada de la marca (.portada-mancha)
          <>
            <span className="portada-mancha portada-mancha--1" aria-hidden="true" />
            <span className="portada-mancha portada-mancha--2" aria-hidden="true" />
            <span className="portada-mancha portada-mancha--3" aria-hidden="true" />
          </>
        ) : (
          <>
            <span className="animated-cover__bubble animated-cover__bubble--1" aria-hidden="true" />
            <span className="animated-cover__bubble animated-cover__bubble--2" aria-hidden="true" />
            <span className="animated-cover__bubble animated-cover__bubble--3" aria-hidden="true" />
          </>
        )}
        {/* Velo para que se lean los botones de arriba. Sobre la portada de la
            marca va solo arriba: abajo oscurecía el celeste y el verde. */}
        <div className={`absolute inset-0 bg-gradient-to-b ${
          professional.cover_photo || professional.professional_type !== 'salud'
            ? 'from-black/30 via-transparent to-black/50'
            : 'from-black/15 via-transparent to-transparent'
        }`}></div>
       
        {/* Navegación */}
        <div className="header-navigation absolute top-0 left-0 right-0 p-4">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            {/* Botón Volver - Desktop */}
            <button
              onClick={() => navigate('/search')}
              className="hidden md:flex items-center text-white/90 hover:text-white transition-colors bg-black/20 backdrop-blur-md px-4 py-2 rounded-xl hover:bg-black/30"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span>Volver a búsqueda</span>
            </button>

            {/* Botón Volver - Mobile (más compacto) */}
            <button
              onClick={() => navigate('/search')}
              // Mismo estilo que el botón del menú de la derecha: el cuadrado
              // negro pesaba sobre la portada clara
              className="md:hidden p-2 rounded-lg bg-white/20 ring-1 ring-white/25 hover:bg-white/30 transition-all"
              aria-label="Volver a la búsqueda"
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
           
            {/* Desktop Menu - Original */}
            <div className="hidden md:flex items-center space-x-3">
              {currentUser ? (
                <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-md px-4 py-2 rounded-xl">
                  <div className="flex items-center space-x-2">
                    {currentUser.picture && (
                      <img
                        src={currentUser.picture}
                        alt="Avatar"
                        className="w-8 h-8 rounded-full border-2 border-white/30"
                      />
                    )}
                    <span className="text-white/95 font-medium">
                      Hola, {currentUser.nombre}
                    </span>
                  </div>
                 
                  <button
  onClick={() => {
    // 1. Si es profesional -> Dashboard Profesional
    if (currentUser.es_profesional) {
      navigate('/dashboard/profesional');
    } 
    // 2. ✅ SI ES ASISTENTE -> Dashboard Asistente (ESTO FALTABA)
    else if (currentUser.isAssistant || currentUser.is_assistant) {
      navigate('/assistant/dashboard');
    } 
    // 3. Si no es ninguno -> Dashboard Paciente
    else {
      navigate('/dashboard/paciente');
    }
  }}
  className="text-white/80 hover:text-white transition-colors text-sm px-3 py-1 rounded-lg hover:bg-white/10"
>
  Mi Dashboard
</button>
                 
                  <button
                    onClick={() => {
                      localStorage.clear();
                      setCurrentUser(null);
                      window.location.reload();
                    }}
                    className="bg-red-500/80 backdrop-blur-sm text-white px-3 py-1 rounded-lg hover:bg-red-600 transition-colors text-sm"
                  >
                    Cerrar sesión
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => navigate('/login')}
                    className="text-white/90 hover:text-white transition-colors px-4 py-2 rounded-xl hover:bg-white/10 backdrop-blur-md"
                  >
                    Iniciar sesión
                  </button>
                  <button
                    onClick={() => navigate('/register')}
                    className="bg-white/20 backdrop-blur-md text-white px-4 py-2 rounded-xl hover:bg-white/30 transition-colors"
                  >
                    Registrarse
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Información del profesional */}
      <div className="relative -mt-20 pb-12">
        <div className="max-w-6xl mx-auto px-4">
          {/* Tarjeta principal */}
                    <div className="professional-main-card ficha-principal glass-card-strong rounded-3xl shadow-2xl p-6 md:p-8 mb-8 landing-reveal landing-reveal--d1">
            <div className="flex flex-col md:flex-row md:items-start md:space-x-8">
              {/* Avatar - clickeable para ampliar */}
              <div className="flex-shrink-0 mx-auto md:mx-0 mb-4 md:mb-0">
                <div
                  className="avatar-premium w-32 h-32 md:w-36 md:h-36 rounded-3xl overflow-hidden border-4 border-white shadow-xl bg-gradient-to-br from-blue-500 to-emerald-500"
                  onClick={() => professional.picture && setShowAvatarLightbox(true)}
                  title={professional.picture ? 'Click para ampliar' : ''}
                >
                  {professional.picture ? (
                    <img
                      src={professional.picture}
                      alt={`${professional.nombre} ${professional.apellido}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white font-bold text-3xl">
                      {getInitials(professional.nombre, professional.apellido)}
                    </div>
                  )}
                </div>
              </div>

              {/* Información */}
              <div className="flex-1 text-center md:text-left">
                <h1 className="texto-equilibrado text-[28px] leading-tight md:text-4xl font-bold text-gray-900 mb-1.5">
                  {professional.business_name || `${professional.nombre} ${professional.apellido}`}
                </h1>
               
                {/* Especialidad en un solo renglón, en el color de la marca. La
                    pastilla "Salud/Belleza" se sacó (oct-2026, Esteban): es una
                    categoría nuestra, para el buscador, y al paciente no le dice
                    nada que la especialidad no diga. Si no cargó especialidad,
                    queda la categoría en su lugar. inline-block: así el degradé
                    del texto va de punta a punta de la palabra. */}
                <p className="mb-4 text-center md:text-left">
                  <span className={`especialidad-marca${professional.professional_type === 'salud' ? '' : ' especialidad-marca--belleza'} inline-block text-base md:text-lg font-semibold`}>
                    {professional.especialidad || (professional.professional_type === 'salud' ? 'Salud' : 'Belleza')}
                  </span>
                </p>

                {/* Información comercial con truncado inteligente */}
                <div className="mb-6">
                  <BusinessInfo
                    businessName={professional.business_name}
                    businessDescription={professional.business_description}
                    businessAddress={professional.business_address}
                    maxDescriptionLength={120}
                    ocultarUbicacionEnMovil
                  />

                  {/* Horarios, Ubicación y Contacto arriba y en solapas, solo
                      en celular. En esa pantalla la columna lateral que los
                      contiene cae DEBAJO de todos los servicios y nadie llega.
                      En escritorio no se renderiza: ahí la lateral ya muestra
                      las tres cosas juntas. */}
                  <div className="mt-4">
                    <FichaMovil
                      professionalId={professional.id}
                      nombre={professional.nombre}
                      telefono={professional.telefono}
                      businessAddress={professional.business_address}
                      sedes={publicLocations}
                      maxAdvanceBooking={maxAdvanceBooking}
                    />
                  </div>
                </div>

                {/* Banner no disponible */}
                {professional.is_available_for_booking === false && (
                  <UnavailableBanner
                    professionalName={professional.business_name || `${professional.nombre} ${professional.apellido}`}
                  />
                )}

                {/* Botones de acción */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                  {professional.is_available_for_booking === false ? (
                    <button
                      disabled
                      className="bg-gray-400 text-gray-100 px-8 py-3 flex items-center gap-2 rounded-xl font-semibold cursor-not-allowed opacity-75"
                      title="Este profesional no está disponible para reservas"
                    >
                      <Lock className="w-5 h-5" /> No Disponible para Reservas
                    </button>
                  ) : (
                    // En el celular este botón no se muestra: la reserva
                    // está en la barra fija de abajo, y dos a la vez sobran.
                    <button
                      onClick={() => handleBookAppointment()}
                      className="btn-premium px-8 py-3.5 text-base hidden md:block"
                    >
                      Agendar Cita
                    </button>
                  )}
                  
                  {/* BOTÓN DE WHATSAPP */}
                  {professional.telefono && (
                    <a
                      href={`https://wa.me/${professional.telefono.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-8 py-3.5 rounded-2xl font-semibold hover:shadow-lg hover:shadow-green-500/25 hover:-translate-y-0.5 transition-all duration-300 flex items-center gap-2"
                    >
                      <MessageCircle className="w-5 h-5" /> WhatsApp
                    </a>
                  )}

                  {/* BOTÓN COMPARTIR PERFIL */}
                  <button
                    onClick={async () => {
                      const shareUrl = `${API_URL}/p/${professional.slug || professional.id}`;
                      const profName = professional.business_name || `${professional.nombre} ${professional.apellido}`;
                      if (navigator.share) {
                        try {
                          await navigator.share({
                            title: `${profName} - consultAR`,
                            text: `Reservá tu turno con ${profName}:`,
                            url: shareUrl,
                          });
                        } catch (e) { /* user cancelled */ }
                      } else {
                        try {
                          await navigator.clipboard.writeText(shareUrl);
                          alert('Link copiado al portapapeles');
                        } catch (e) {
                          // fallback
                          const ta = document.createElement('textarea');
                          ta.value = shareUrl;
                          document.body.appendChild(ta);
                          ta.select();
                          document.execCommand('copy');
                          document.body.removeChild(ta);
                          alert('Link copiado al portapapeles');
                        }
                      }
                    }}
                    className="glass-card text-gray-700 px-6 py-3.5 rounded-2xl font-semibold hover:-translate-y-0.5 transition-all duration-300 flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                    Compartir
                  </button>

                  {/* BOTÓN STORIES */}
                  <button
                    onClick={() => setShowStoryGenerator(true)}
                    className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white px-6 py-3.5 rounded-2xl font-semibold hover:shadow-lg hover:shadow-pink-500/25 hover:-translate-y-0.5 transition-all duration-300 flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                    </svg>
                    Stories
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Secciones de contenido */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Columna principal (2/3) */}
            <div className="lg:col-span-2 space-y-8">
              {/* Servicios */}
              {services.length > 0 && (
                <section
                  ref={refSeccionServicios}
                  className="glass-card-strong rounded-3xl p-4 md:p-8 landing-reveal landing-reveal--d2 text-left"
                >
                  <h2 className="section-title mb-3 md:mb-4">Servicios</h2>
                  {/* Tarjetas: el porqué del diseño y la jerarquía están en
                      ListaServicios.tsx. */}
                  <ListaServicios
                    servicios={services}
                    politicaDePago={paymentConfig}
                    disponible={professional.is_available_for_booking !== false}
                    onAgendar={(servicio) => handleBookAppointment(servicio)}
                  />
                </section>
              )}

              {/* Portfolio */}
              {portfolio.length > 0 && (
                <section className="glass-card-strong rounded-3xl p-6 md:p-8 landing-reveal landing-reveal--d3">
                  <h2 className="section-title mb-6">Portfolio</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {portfolio.map((item, index) => (
                      <div 
                        key={item.id} 
                        className="border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow cursor-pointer group"
                        onClick={() => openLightbox(index)} // ✅ NUEVO: Abrir lightbox al hacer click
                      >
                        <div className="aspect-video bg-gray-100 overflow-hidden relative">
                          <img
                            src={item.image_url}
                            alt={item.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                          {/* ✅ NUEVO: Ícono de zoom al hover */}
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                            <svg className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                            </svg>
                          </div>
                        </div>
                        <div className="p-4">
                          <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                          {item.description && (
                            <p className="text-gray-600 text-sm">{item.description}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>

            {/* Sidebar (1/3) — oculta en celular: ahí lo mismo se muestra
                arriba en solapas (FichaMovil), y duplicarlo haría scrollear
                de más para leer dos veces lo mismo. */}
            <div className="space-y-6 hidden lg:block">
              {/* Información de contacto */}
              <div className="sidebar-glass rounded-3xl p-6 landing-reveal landing-reveal--d2">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Contacto</h3>
               
                {professional.telefono && (
                  <div className="flex items-center mb-3">
                    <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span className="text-gray-600">{professional.telefono}</span>
                  </div>
                )}

                {/* ✅ Mostrar sedes si hay, sino la dirección del negocio */}
                {publicLocations.length > 0 ? (
                  <div className="space-y-3">
                    {publicLocations.map((loc: any) => {
                      const fullAddress = [loc.address, loc.city].filter(Boolean).join(', ');
                      return (
                      <div key={loc.id} className="flex items-start">
                        <svg className="w-5 h-5 text-gray-400 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <span className="text-gray-700 font-medium">{loc.name}</span>
                          {loc.city && <span className="text-gray-500 text-sm"> — {loc.city}</span>}
                          {loc.address && (
                            fullAddress ? (
                              <a
                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-500 hover:text-blue-600 text-sm flex items-center gap-1 group transition-colors"
                                title="Abrir en Google Maps"
                              >
                                <span className="underline-offset-2 group-hover:underline">{loc.address}</span>
                                <svg className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              </a>
                            ) : (
                              <div className="text-gray-500 text-sm">{loc.address}</div>
                            )
                          )}
                          {loc.phone && (
                            <a
                              href={`https://wa.me/${loc.phone.replace(/\D/g, '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-green-600 hover:text-green-700 text-sm mt-1 font-medium"
                            >
                              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                              </svg>
                              {loc.phone}
                            </a>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                ) : professional.business_address ? (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(professional.business_address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center group hover:bg-blue-50 rounded-lg p-2 -m-2 transition-colors"
                    title="Abrir en Google Maps"
                  >
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-500 mr-3 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="text-gray-600 group-hover:text-blue-700 transition-colors flex-1 group-hover:underline underline-offset-2">{professional.business_address}</span>
                    <svg className="w-4 h-4 ml-2 text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                ) : null}

                {/* Botón WhatsApp: si hay sedes con teléfono propio, ya se muestran arriba por sede */}
                {/* Si no hay sedes con tel o solo hay 1 sede, mostrar botón genérico */}
                {(() => {
                  const sedesConTel = publicLocations.filter((l: any) => l.phone);
                  // Si ya se muestran teléfonos por sede (múltiples), no duplicar botón genérico
                  if (sedesConTel.length > 1) return null;
                  // Si hay una sola sede con tel, usar ese
                  const whatsappPhone = sedesConTel.length === 1 ? sedesConTel[0].phone : professional.telefono;
                  if (!whatsappPhone) return null;
                  return (
                    <button
                      onClick={() => {
                        const message = encodeURIComponent(
                          `Hola ${professional.nombre}, vi tu perfil en ConsultAR y me interesa agendar una cita. ¿Podrías darme más información?`
                        );
                        const phone = whatsappPhone.replace(/\D/g, '');
                        window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
                      }}
                      className="w-full mt-4 bg-green-500 text-white py-2 rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center"
                    >
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.150-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.150-1.255-.463-2.39-1.475-.883-.788-1.480-1.761-1.653-2.059-.173-.297-.018-.458.130-.606.134-.133.298-.347.446-.520.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.520-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.010-.570-.010-.198 0-.520.074-.792.372-.272.297-1.040 1.016-1.040 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.200 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.570-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                      </svg>
                      Contactar por WhatsApp
                    </button>
                  );
                })()}
              </div>

              {/* Horarios de atención */}
              <div className="sidebar-glass rounded-3xl p-6 landing-reveal landing-reveal--d3">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Horarios</h3>
                
                {/* Un solo bloque para todos los casos.
                    Antes acá convivían dos: el multi-sede y uno viejo que
                    decía "Disponible / No disponible" a secas. Esa palabra no
                    le sirve a nadie: quien mira quiere saber SI puede venir a
                    las 9, y "Disponible" no lo contesta.
                    Ahora se muestran las horas reales, y con varias sedes
                    también dónde. Con una o ninguna, el nombre de la sede no
                    se dibuja. */}
                {professional?.id && (
                  <HorariosMultiSede
                    professionalId={professional.id}
                    sedes={publicLocations}
                    maxAdvanceBooking={maxAdvanceBooking}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de selección de servicio */}
      {showServiceSelector && (
        <div
          className="fixed inset-0 bg-black/50 z-50 overflow-y-auto animate-backdrop-in"
          style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowServiceSelector(false); }}
        >
          <div className="flex items-start md:items-center justify-center min-h-full p-4 py-8">
            {/* text-left: la app centra todo por defecto (.App de CRA) */}
            <div className="booking-modal-content rounded-3xl max-w-md w-full p-6 animate-modal-in text-left" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-xl font-bold text-slate-900">Elegí un servicio</h3>
              <p className="text-sm text-slate-500 mt-1 mb-5">Después elegís el día y el horario.</p>

              <div className="space-y-3 mb-6 max-h-[60vh] overflow-y-auto pr-1">
                {services.map((service) => (
                  <div
                    key={service.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleServiceSelection(service)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleServiceSelection(service); } }}
                    className="group relative cursor-pointer w-full text-left p-4 rounded-2xl bg-white border border-slate-200 shadow-sm hover:border-sky-300 hover:shadow-md transition-all"
                  >
                    <div className="flex flex-wrap justify-between items-start mb-2 gap-x-3 gap-y-1.5">
                      <h4 className="font-bold text-gray-900 leading-snug">{service.name}</h4>
                      <span className="text-[15px] font-bold text-slate-900 flex-shrink-0 whitespace-nowrap">{displayPrice(service)}</span>
                    </div>
                    {service.description && (
                      <TextoExpandible
                        text={service.description}
                        limit={90}
                        className="text-sm text-gray-500 mb-2 leading-relaxed"
                      />
                    )}
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-sky-700 bg-sky-50 px-2.5 py-1 rounded-full">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {service.duration} min
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-sky-700 opacity-80 group-hover:opacity-100 transition-opacity">
                        Elegir
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={() => setShowServiceSelector(false)}
                className="w-full glass-card text-gray-600 py-3 rounded-2xl font-medium hover:bg-white/80 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de agendamiento */}
      {showBookingModal && selectedService && (
        <div
          className="fixed inset-0 bg-black/50 z-50 overflow-y-auto animate-backdrop-in"
          style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}
          onClick={(e) => { if (e.target === e.currentTarget) { setShowBookingModal(false); setSelectedService(null); } }}
        >
          <div className="flex items-start md:items-center justify-center min-h-full p-3 md:p-4 py-6">
          {/* text-left: la app centra todo por defecto (.App de CRA), y acá
              quedaban centrados el servicio y las etiquetas de los campos */}
          <div className="bg-white rounded-3xl max-w-4xl w-full shadow-2xl animate-modal-in text-left">
            {/* Cabecera: en salud, con los colores de la marca, como la portada */}
            <div className={`bg-gradient-to-r ${professional?.professional_type === 'salud' ? 'from-blue-600 via-sky-600 to-teal-600' : 'from-blue-600 via-indigo-600 to-purple-600'} rounded-t-3xl p-5 md:p-6`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-white text-lg md:text-xl font-bold">Agendar Cita</h3>
                    <p className="text-white/70 text-xs">
                      {professional?.business_name || `${professional?.nombre} ${professional?.apellido}`}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => { setShowBookingModal(false); setSelectedService(null); }}
                  className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors"
                >
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <form onSubmit={handleBookingSubmit} className="p-5 md:p-6">
              {/* Servicios + Profesional */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {/* Servicios */}
                <div className="tarjeta-modal rounded-2xl p-4 border">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="icono-seccion w-6 h-6 rounded-lg flex items-center justify-center">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </span>
                    <h4 className="font-bold text-gray-900 text-sm">Servicios</h4>
                  </div>
                  {/* Servicio principal */}
                  <div className="flex items-start justify-between gap-3 py-2">
                    <span className="font-semibold text-slate-900 text-sm leading-snug min-w-0">{selectedService.name}</span>
                    <span className="text-[13px] text-slate-500 whitespace-nowrap flex-shrink-0">{selectedService.duration} min · {displayPrice(selectedService)}</span>
                  </div>
                  {/* Adicionales */}
                  {additionalServices.map(s => (
                    <div key={s.id} className="flex items-start justify-between gap-3 py-2 border-t border-slate-200/70">
                      <span className="font-semibold text-slate-900 text-sm leading-snug min-w-0">{s.name}</span>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-[13px] text-slate-500 whitespace-nowrap">{s.duration} min · {formatPrice(s.price)}</span>
                        <button type="button" onClick={() => toggleAdditionalService(s)} className="w-5 h-5 bg-red-100 hover:bg-red-200 text-red-500 rounded-full flex items-center justify-center text-xs transition-colors">&times;</button>
                      </div>
                    </div>
                  ))}
                  {/* Total */}
                  <div className="flex items-center justify-between pt-3 mt-2 border-t border-slate-200">
                    <span className="font-bold text-slate-900 text-sm">Total</span>
                    <span className="total-modal font-bold whitespace-nowrap">
                      {totalDuration} min · {totalPrice > 0
                        ? formatPrice(totalPrice)
                        : (allSelectedServices.every(s => s.price_mode === 'obra_social')
                            ? 'Obra social'
                            : 'A consultar')}
                    </span>
                  </div>
                  {/* Agregar mas — deshabilitado si el servicio elegido tiene horario exclusivo */}
                  {selectedService.has_custom_schedule ? (
                    <div className="mt-3 pt-3 border-t border-dashed border-blue-200/50">
                      <p className="text-[11px] text-gray-500 flex items-center gap-1.5">
                        <svg className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Este servicio se agenda solo, en su horario específico.
                      </p>
                    </div>
                  ) : (
                    services.length > 1 && services.filter(s => s.id !== selectedService.id && !additionalServices.find(a => a.id === s.id) && !s.has_custom_schedule).length > 0 && (
                      <div className="mt-3 pt-3 border-t border-dashed border-blue-200/50">
                        {!showAddServices ? (
                          // Colapsado: solo un botón "+" para no alargar el modal en celular
                          <button
                            type="button"
                            onClick={() => setShowAddServices(true)}
                            className="flex items-center gap-1.5 text-sm font-semibold text-sky-700 hover:text-sky-800"
                          >
                            <span className="w-5 h-5 rounded-full bg-sky-100 flex items-center justify-center text-sky-700 leading-none">+</span>
                            Agregar otro servicio
                          </button>
                        ) : (
                          <>
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold">Agregar servicio</p>
                              <button type="button" onClick={() => setShowAddServices(false)}
                                className="text-[11px] text-gray-400 hover:text-gray-600 font-medium">
                                Ocultar
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {services
                                .filter(s => s.id !== selectedService.id && !additionalServices.find(a => a.id === s.id) && !s.has_custom_schedule)
                                .map(s => (
                                  <button key={s.id} type="button" onClick={() => toggleAdditionalService(s)}
                                    className="text-xs px-3 py-1.5 rounded-full bg-white border border-blue-200 text-blue-600 hover:bg-blue-500 hover:text-white hover:border-blue-500 transition-all duration-200 font-medium">
                                    + {s.name}
                                  </button>
                                ))}
                            </div>
                          </>
                        )}
                      </div>
                    )
                  )}
                </div>

                {/* Profesional */}
                <div className="tarjeta-modal rounded-2xl p-4 border">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="icono-seccion w-6 h-6 rounded-lg flex items-center justify-center">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </span>
                    <h4 className="font-bold text-gray-900 text-sm">Profesional</h4>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-emerald-500 overflow-hidden flex-shrink-0 shadow-md">
                      {professional?.picture ? (
                        <img src={professional.picture} alt={professional.nombre} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white font-bold text-lg">
                          {professional && getInitials(professional.nombre, professional.apellido)}
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">
                        {professional?.business_name || `${professional?.nombre} ${professional?.apellido}`}
                      </p>
                      {professional?.especialidad && (
                        <p className={`especialidad-marca${professional.professional_type === 'salud' ? '' : ' especialidad-marca--belleza'} inline-block text-sm font-semibold`}>{professional.especialidad}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Datos del paciente */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="icono-seccion w-6 h-6 rounded-lg flex items-center justify-center">
                    <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </span>
                  <h4 className="font-bold text-gray-900 text-sm">Tus datos</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[13px] font-semibold text-slate-600 mb-1.5">
                      Nombre completo *
                    </label>
                    <input type="text" value={bookingForm.clientName}
                      onChange={(e) => setBookingForm({...bookingForm, clientName: e.target.value})}
                      className="w-full input-premium" placeholder="Tu nombre completo" required />
                  </div>
                  <div>
                    <label className="block text-[13px] font-semibold text-slate-600 mb-1.5">
                      WhatsApp *
                    </label>
                    <input type="tel" value={bookingForm.clientPhone}
                      onChange={(e) => setBookingForm({...bookingForm, clientPhone: e.target.value})}
                      className="w-full input-premium" placeholder="+54 11 2345-6789" required />
                  </div>
                  <div>
                    <label className="block text-[13px] font-semibold text-slate-600 mb-1.5">
                      Email <span className="text-slate-400 font-normal">(opcional)</span>
                    </label>
                    <input type="email" value={bookingForm.clientEmail}
                      onChange={(e) => setBookingForm({...bookingForm, clientEmail: e.target.value})}
                      className="w-full input-premium" placeholder="Si no tenés, dejalo vacío" />
                  </div>
                  <div>
                    <label className="block text-[13px] font-semibold text-slate-600 mb-1.5">
                      ¿Primera consulta?
                    </label>
                    <select value={bookingForm.isFirstTime.toString()}
                      onChange={(e) => setBookingForm({...bookingForm, isFirstTime: e.target.value === 'true'})}
                      className="w-full input-premium">
                      <option value="true">Sí, primera vez</option>
                      <option value="false">No, ya vine antes</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-[13px] font-semibold text-slate-600 mb-1.5">Notas</label>
                    <textarea value={bookingForm.notes}
                      onChange={(e) => setBookingForm({...bookingForm, notes: e.target.value})}
                      rows={2} className="w-full input-premium" placeholder="Información adicional…" />
                  </div>
                </div>
              </div>

              {/* ✅ Selector de sede (solo si hay más de 1 sede activa) */}
              {publicLocations.length > 1 && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="icono-seccion w-6 h-6 rounded-lg flex items-center justify-center">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </span>
                    <h4 className="font-bold text-gray-900 text-sm">Sede *</h4>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {sedesDelServicio.map((loc) => (
                      <button
                        key={loc.id}
                        type="button"
                        onClick={() => setSelectedLocationId(loc.id)}
                        className={`text-left p-4 rounded-2xl border-2 transition-all duration-200 ${
                          selectedLocationId === loc.id
                            ? 'border-teal-500 bg-teal-50 shadow-md'
                            : 'border-gray-200 bg-white hover:border-teal-300 hover:bg-teal-50/50'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                            selectedLocationId === loc.id ? 'border-teal-500' : 'border-gray-300'
                          }`}>
                            {selectedLocationId === loc.id && (
                              <div className="w-3 h-3 rounded-full bg-teal-500"></div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900 text-sm">{loc.name}</p>
                            {loc.address && (
                              <p className="text-xs text-gray-500 mt-0.5 truncate">{loc.address}</p>
                            )}
                            {loc.city && (
                              <p className="text-xs text-gray-400">{loc.city}</p>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* ✅ Campos dinámicos del profesional */}
              {bookingExtraFields?.enabled && bookingExtraFields.fields?.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="icono-seccion w-6 h-6 rounded-lg flex items-center justify-center">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </span>
                    <h4 className="font-bold text-gray-900 text-sm">Información adicional</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {bookingExtraFields.fields.map((field) => (
                      <div key={field.key} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                        <label className="block text-[13px] font-semibold text-slate-600 mb-1.5">
                          {field.label} {field.required && <span className="text-red-400">*</span>}
                        </label>
                        {field.type === 'textarea' ? (
                          <textarea
                            value={extraFieldValues[field.key] || ''}
                            onChange={(e) => setExtraFieldValues(prev => ({ ...prev, [field.key]: e.target.value }))}
                            rows={2}
                            className="w-full input-premium"
                            placeholder={field.label}
                            required={field.required}
                          />
                        ) : field.type === 'select' && field.options ? (
                          <select
                            value={extraFieldValues[field.key] || ''}
                            onChange={(e) => setExtraFieldValues(prev => ({ ...prev, [field.key]: e.target.value }))}
                            className="w-full input-premium"
                            required={field.required}
                          >
                            <option value="">Seleccionar...</option>
                            {field.options.map((opt) => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        ) : field.type === 'date' ? (
                          <input
                            type="date"
                            value={extraFieldValues[field.key] || ''}
                            onChange={(e) => setExtraFieldValues(prev => ({ ...prev, [field.key]: e.target.value }))}
                            className="w-full input-premium"
                            required={field.required}
                          />
                        ) : (
                          <input
                            type="text"
                            value={extraFieldValues[field.key] || ''}
                            onChange={(e) => setExtraFieldValues(prev => ({ ...prev, [field.key]: e.target.value }))}
                            className="w-full input-premium"
                            placeholder={field.label}
                            required={field.required}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Fecha y hora */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="icono-seccion w-6 h-6 rounded-lg flex items-center justify-center">
                    <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </span>
                  <h4 className="font-bold text-gray-900 text-sm">Fecha y hora</h4>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Calendario */}
                  <div className="space-y-3">
                    <label className="block text-[13px] font-semibold text-slate-600">
                      Elegí una fecha *
                    </label>
                  <CustomCalendar
                    selectedDate={bookingForm.appointmentDate}
                    onDateSelect={(date) => {
                      setBookingForm({...bookingForm, appointmentDate: date, appointmentTime: ''});
                      // ✅ FIX: Usar professional.id en vez de id
                      if (professional?.id) {
                        loadTimeSlotsForDate(professional.id, date, selectedLocationId);
                      }
                    }}
                    professionalSchedule={professionalSchedule}
                    blockedDates={blockedDates}
                    specialDayDates={specialDayDates}
                    onlySpecialDays={onlySpecialDays}
                    serviceAvailableDates={serviceAvailableDates}
                    minDate={(() => { const t = new Date(); return `${t.getFullYear()}-${(t.getMonth() + 1).toString().padStart(2, '0')}-${t.getDate().toString().padStart(2, '0')}`; })()}
                    maxDate={(() => {
                      const d = new Date();
                      d.setDate(d.getDate() + maxAdvanceBooking);
                      return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
                    })()}
                  />
                  {bookingForm.appointmentDate && (
                    <div className="mt-3 px-3 py-2 bg-sky-50 border border-sky-100 rounded-xl">
                      <p className="text-sm text-sky-800 font-medium first-letter:uppercase">
                        Fecha seleccionada: {(() => {
                          const [year, month, day] = bookingForm.appointmentDate.split('-');
                          const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                          return date.toLocaleDateString('es-ES', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          });
                        })()}
                      </p>
                    </div>
                  )}
                </div>

                {/* Horarios disponibles */}
                <div>
                  <label className="block text-[13px] font-semibold text-slate-600 mb-2">
                    Horarios disponibles *
                  </label>
                  {bookingForm.appointmentDate ? (
                    loadingTimeSlots ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full mr-2"></div>
                        <span className="text-sm text-gray-600">Cargando horarios...</span>
                      </div>
                    ) : availableTimeSlots.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2">
                        {availableTimeSlots.map((time) => (
                          <button
                            key={time}
                            type="button"
                            onClick={() => setBookingForm({...bookingForm, appointmentTime: time})}
                            className={`time-slot text-sm ${
                              bookingForm.appointmentTime === time
                                ? 'time-slot-selected'
                                : ''
                            }`}
                          >
                            {time}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="text-sm text-red-800">No hay horarios disponibles para esta fecha</p>
                        <p className="text-xs text-red-600 mt-1">Por favor selecciona otra fecha</p>
                      </div>
                    )
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-8 text-center">
                      <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-gray-600">Selecciona una fecha para ver los horarios disponibles</p>
                    </div>
                  )}
                  <input
                    type="hidden"
                    name="appointmentTime"
                    value={bookingForm.appointmentTime}
                    required
                  />
                </div>
              </div>
              </div>

              {/* Botones de acción */}
              <div className="flex space-x-4 pt-6 border-t border-white/30">
                <button
                  type="button"
                  onClick={() => {
                    setShowBookingModal(false);
                    setSelectedService(null);
                  }}
                  className="flex-1 glass-card text-gray-600 px-4 py-3.5 rounded-2xl font-medium hover:bg-white/80 transition-all"
                >
                  Cancelar
                </button>

                {/* Más ancho que Cancelar y en un renglón: en el celular se partía
                    en "Confirmar / Cita" */}
                <button
                  type="submit"
                  disabled={bookingLoading || !bookingForm.appointmentDate || !bookingForm.appointmentTime}
                  className="flex-[1.6] btn-premium whitespace-nowrap !px-4"
                >
                  {bookingLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                      Procesando...
                    </div>
                  ) : (
                    'Confirmar Cita'
                  )}
                </button>
              </div>
            </form>
          </div>
          </div>
        </div>
      )}

{/* Modal de paso de pago */}
{showPaymentStep && selectedService && professional && (
  <div className="fixed inset-0 booking-modal-overlay flex items-center justify-center z-50 p-4 animate-backdrop-in">
    <div className="booking-modal-content rounded-3xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto animate-modal-in">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900">
          Método de pago
        </h2>
        <button
          onClick={handlePaymentBack}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      <PaymentStep
        professionalId={professional.id} // ✅ FIX: Usar professional.id
        servicePrice={selectedService.price} // El precio real se calcula en PaymentStep con la data de SessionStorage
        appointmentId={null}
        onPaymentMethodSelected={handlePaymentComplete} // ✅ CAMBIADO: Ahora usa handlePaymentComplete
        onBack={handlePaymentBack}
      />
    </div>
  </div>
)}

      {/* Modal de éxito */}
      {showSuccessModal && bookedAppointment && (
        <div className="fixed inset-0 booking-modal-overlay flex items-center justify-center z-50 p-4 animate-backdrop-in">
          <div className="booking-modal-content rounded-3xl max-w-md w-full max-h-[90vh] overflow-y-auto p-6 animate-modal-in">
            <div className="text-center mb-6">
              {/* Icono y título dinámico según método de pago */}
              {bookedAppointment.paymentInfo?.method === 'transferencia' ? (
                <>
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">¡Turno Pendiente!</h3>
                  <p className="text-gray-600">Tu turno será confirmado cuando verifiquemos el pago</p>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 success-pulse">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">¡Cita Agendada!</h3>
                  <p className="text-gray-600">Tu consulta ha sido programada exitosamente</p>
                </>
              )}
            </div>

            {/* Aviso especial para transferencias */}
            {bookedAppointment.paymentInfo?.method === 'transferencia' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <h4 className="text-sm font-semibold text-amber-800 mb-1">Pago por transferencia</h4>
                    <p className="text-xs text-amber-700">
                      El turno se confirmará cuando el profesional verifique tu comprobante. Recibirás un email de confirmación.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="glass-card rounded-2xl p-4 mb-6 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">Profesional:</span>
                <span className="text-sm text-gray-900">{bookedAppointment.professionalName}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">Servicio:</span>
                <span className="text-sm text-gray-900">{bookedAppointment.serviceName}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">Fecha:</span>
                <span className="text-sm text-gray-900 capitalize">{bookedAppointment.formattedDate}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">Hora:</span>
                <span className="text-sm font-bold text-blue-600">{bookedAppointment.appointment_time}</span>
              </div>
              
              {bookedAppointment.locationName && (
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Sede:</span>
                  <span className="text-sm text-gray-900">{bookedAppointment.locationName}</span>
                </div>
              )}

              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">Precio Total:</span>
                <span className="text-sm font-bold text-green-600">{bookedAppointment.formattedPrice}</span>
              </div>

              {/* === SECCIÓN DE PAGO CORREGIDA Y CENTRALIZADA === */}
              {bookedAppointment.paymentInfo && bookedAppointment.paymentInfo.tipoCobro !== 'efectivo' && (
                <div className="pt-2 border-t">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600">
                      {bookedAppointment.paymentInfo.tipoCobro === 'solo_sena' ? 'Seña Pagada:' : 'Monto Pagado:'}
                    </span>
                    <span className="text-sm font-bold text-orange-600">
                      {formatPrice(bookedAppointment.paymentInfo.amount)}
                      {bookedAppointment.paymentInfo.porcentajeSena && bookedAppointment.paymentInfo.tipoCobro === 'solo_sena' && (
                        ` (${bookedAppointment.paymentInfo.porcentajeSena}%)`
                      )}
                    </span>
                  </div>
                  {bookedAppointment.paymentInfo.tipoCobro === 'solo_sena' && (
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-xs text-gray-500">Saldo pendiente:</span>
                      <span className="text-xs text-gray-500">
                        {formatPrice(bookedAppointment.price - bookedAppointment.paymentInfo.amount)}
                      </span>
                    </div>
                  )}
                </div>
              )}
              {bookedAppointment.paymentInfo && bookedAppointment.paymentInfo.tipoCobro === 'efectivo' && (
                <div className="pt-2 border-t">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600">Método de pago:</span>
                    <span className="text-sm font-bold text-gray-700">Abonás en consultorio (efectivo o transferencia)</span>
                  </div>
                </div>
              )}
              {/* ============================================== */}
            </div>

            <div className="bg-blue-50 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h4 className="text-sm font-semibold text-blue-900 mb-1">Te contactaremos pronto</h4>
                  <p className="text-xs text-blue-800">
                    Recibirás la confirmación por WhatsApp en {bookedAppointment.client_phone || 'tu teléfono'}.
                    Si tienes alguna pregunta, puedes contactar directamente al profesional.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  setBookedAppointment(null);
                }}
                className="flex-1 bg-gray-100 text-gray-700 px-4 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Cerrar
              </button>
              
              {professional?.telefono && (
                <button
                  onClick={() => {
                    const message = encodeURIComponent(
                      `Hola ${professional.nombre}, acabo de agendar una cita para ${bookedAppointment.formattedDate} a las ${bookedAppointment.appointment_time}. Mi nombre es ${bookedAppointment.client_name}.`
                    );
                    const phone = professional.telefono!.replace(/\D/g, '');
                    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
                    setShowSuccessModal(false);
                    setBookedAppointment(null);
                  }}
                  className="flex-1 bg-green-500 text-white px-4 py-3 rounded-lg font-medium hover:bg-green-600 transition-colors flex items-center justify-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.150-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.150-1.255-.463-2.39-1.475-.883-.788-1.480-1.761-1.653-2.059-.173-.297-.018-.458.130-.606.134-.133.298-.347.446-.520.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.520-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.010-.570-.010-.198 0-.520.074-.792.372-.272.297-1.040 1.016-1.040 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.200 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.570-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                  </svg>
                  Contactar por WhatsApp
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de sugerencia de registro */}
      <RegistrationSuggestionModal
        isOpen={showRegistrationModal}
        onClose={handleCloseRegistrationModal}
        onRegister={handleRegisterFromModal}
        clientName={bookingForm.clientName || 'Usuario'}
        appointmentCount={previousAppointments}
      />

      {/* ✅ NUEVO: Lightbox para Portfolio */}
      {showLightbox && portfolio.length > 0 && (
        <div
          className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center p-4 animate-backdrop-in"
          onClick={closeLightbox}
        >
          {/* Botón cerrar */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Flecha izquierda */}
          {portfolio.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToPrevImage();
              }}
              className="absolute left-4 text-white hover:text-gray-300 transition-colors p-2 bg-black bg-opacity-50 rounded-full"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Contenedor de imagen */}
          <div 
            className="max-w-6xl max-h-[90vh] w-full flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Imagen */}
            <div className="relative w-full flex-1 flex items-center justify-center mb-4">
              <img
                src={portfolio[currentImageIndex].image_url}
                alt={portfolio[currentImageIndex].title}
                className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-2xl"
              />
            </div>

            {/* Info de la imagen */}
            <div className="bg-black bg-opacity-70 backdrop-blur-sm rounded-lg p-4 max-w-2xl w-full">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-white text-xl font-semibold mb-2">
                    {portfolio[currentImageIndex].title}
                  </h3>
                  {portfolio[currentImageIndex].description && (
                    <p className="text-gray-300 text-sm">
                      {portfolio[currentImageIndex].description}
                    </p>
                  )}
                  {portfolio[currentImageIndex].category && (
                    <span className="inline-block mt-2 px-3 py-1 bg-blue-500 bg-opacity-30 text-blue-200 text-xs rounded-full">
                      {portfolio[currentImageIndex].category}
                    </span>
                  )}
                </div>
                <div className="text-white text-sm ml-4 flex-shrink-0">
                  {currentImageIndex + 1} / {portfolio.length}
                </div>
              </div>
            </div>
          </div>

          {/* Flecha derecha */}
          {portfolio.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToNextImage();
              }}
              className="absolute right-4 text-white hover:text-gray-300 transition-colors p-2 bg-black bg-opacity-50 rounded-full"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          {/* Indicador de teclas (solo desktop) */}
          <div className="hidden md:block absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-xs opacity-50">
            ESC para cerrar • ← → para navegar
          </div>
        </div>
      )}

      {/* Avatar Lightbox */}
      {showAvatarLightbox && professional?.picture && (
        <div className="avatar-lightbox" onClick={() => setShowAvatarLightbox(false)}>
          <div className="avatar-lightbox-backdrop" />
          <div className="avatar-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img
              src={professional.picture}
              alt={`${professional.nombre} ${professional.apellido}`}
              className="avatar-lightbox-img"
            />
            <div className="text-center mt-4">
              <h3 className="text-white text-xl font-bold">
                {professional.business_name || `${professional.nombre} ${professional.apellido}`}
              </h3>
              <p className="text-white/60 text-sm mt-1">{professional.especialidad}</p>
            </div>
            <button
              onClick={() => setShowAvatarLightbox(false)}
              className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors bg-black/30 rounded-full p-2"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Story Card Generator */}
      {professional && (
        <StoryCardGenerator
          isOpen={showStoryGenerator}
          onClose={() => setShowStoryGenerator(false)}
          professional={{
            id: professional.id,
            nombre: professional.nombre,
            apellido: professional.apellido,
            especialidad: professional.especialidad,
            professional_type: professional.professional_type,
            business_name: professional.business_name,
            picture: professional.picture,
            slug: professional.slug || undefined,
          }}
        />
      )}

      {/* ── BARRA FIJA DE RESERVA (solo celular) ──
          En el celular el botón "Agendar Cita" quedaba debajo del pliegue:
          en la página de Adolfo Gallo, a casi pantalla y media, porque arriba
          van la descripción y las solapas de horarios. Achicar eso no
          alcanzaba, porque depende de cuánto cargue cada profesional. Así que
          en el celular la acción principal vive acá, siempre a la vista.

          Con servicios, la barra se va al llegar a ellos (ver
          enServiciosOMasAbajo), así que al final de la página nunca está. Sin
          servicios puede estar hasta abajo: ahí el espaciador deja que lo
          último suba por encima de ella. No depende de los modales: si
          desapareciera al abrir uno, la página cambiaría de alto detrás. */}
      {reservable && services.length === 0 && <div className="h-24 md:hidden" aria-hidden="true" />}
      {mostrarBarraReserva && (
        <div
          className="md:hidden fixed inset-x-0 bottom-0 z-40 bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.06)] px-4 pt-3 animate-slide-up"
          style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
        >
          <button
            onClick={() => handleBookAppointment()}
            className="btn-premium w-full py-3.5 text-base"
          >
            Agendar Cita
          </button>
        </div>
      )}
    </div>
  );
};
export default PublicProfessionalLanding;